import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Dialog,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Badge,
  Grow,
  Drawer,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  CardContent,
  Tooltip,
  Grid,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  keyframes,
} from '@mui/material'
// OPTIMIZATION: Direct import for tree-shaking
import debounce from 'lodash/debounce'
import { motion, AnimatePresence } from 'framer-motion'
import { RoomType } from '../../../types/Rooms'

// Feature flag: set to false to re-enable custom room creation

function roomMetadataRequiresPassword(metadata?: Record<string, unknown> | null): boolean {
  if (!metadata) return false
  return !!(metadata.hasPassword || metadata.isPrivate || metadata.passwordProtected)
}

// Animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

// Icons
import LogoutIcon from '@mui/icons-material/Logout'
import DashboardIcon from '@mui/icons-material/Dashboard'
import GroupsIcon from '@mui/icons-material/Groups'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import PersonIcon from '@mui/icons-material/Person'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import HomeIcon from '@mui/icons-material/Home'
import RefreshIcon from '@mui/icons-material/Refresh'
import PeopleIcon from '@mui/icons-material/People'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import EditIcon from '@mui/icons-material/Edit'
import GroupIcon from '@mui/icons-material/Group'
import TimelineIcon from '@mui/icons-material/Timeline'
import MemoryIcon from '@mui/icons-material/Memory'
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications'
import CategoryIcon from '@mui/icons-material/Category'
import BusinessIcon from '@mui/icons-material/Business'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ScheduleIcon from '@mui/icons-material/Schedule'
import MapIcon from '@mui/icons-material/Map'
import BugReportIcon from '@mui/icons-material/BugReport'
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import EventNoteIcon from '@mui/icons-material/EventNote'
import ChatIcon from '@mui/icons-material/Chat'
import { isGuestUserId } from './consent/TermsGate'

// Components
import ProfileEditDialog from './ProfileEditDialog'
import RoomCard from './RoomCard'
import DashboardRoomList from './DashboardRoomList'
import { dashboardGreeting } from '../utils/dashboardGreeting'
import UpcomingMeetingsList from './RoomBooking/UpcomingMeetingsList'
import ProfileAvatar, { profileAvatarPropsOf } from './ProfileAvatar'
import AdminMetrics from './AdminMetrics'
import { PermissionsDialog } from './PermissionsDialog'
import WarningIcon from '@mui/icons-material/Warning'
import AdminEnvironmentConfig from './MainMenu/sections/admin/AdminEnvironmentConfig'
import AdminProductUpdatesPanel from './MainMenu/sections/admin/AdminProductUpdatesPanel'
import AdminBillingSettings from './MainMenu/sections/admin/AdminBillingSettings'
import ProfileMainMenuPanel from './MainMenu/sections/profile/ProfileMainMenuPanel'
import UserManagementCard from './AdminPanel/UserManagementCard'
import EndpointTestingPanel from './AdminPanel/EndpointTestingPanel'
import ServerJobsPanel from './ServerJobsPanel'
import AdminMaps from './MainMenu/sections/admin/AdminMaps'
import TransactionalEmailAuditPanel from './admin/TransactionalEmailAuditPanel'
// lobbyService used below

import EntityCatalogueAdminPanel from './EntityCatalogueAdminPanel'
import MeetingsDashboardPanel from './RoomBooking/MeetingsDashboardPanel'

import ChatPage from './ChatPanel/ChatPage'
import { ChatNavDot } from './GameMenu/ChatNavDot'
import { requestNotificationPermission } from '../utils/notifications'
import BookingFormDialog from './RoomBooking/BookingFormDialog'
import CompanyManagementPanel from './CompanyManagement'
import { CompanyRoomsDialog } from './CompanyManagement/components/dialogs/CompanyRoomsDialog'
import Parse from 'parse'
import SubscriptionRequiredOverlay from './common/SubscriptionRequiredOverlay'
import type { MainMenuSection } from './MainMenu/types/MainMenu.types'
import {
  getMainMenuSectionLabel,
  getMainMenuNavScrollSection,
} from './MainMenu/utils/mainMenuSectionLabels'
import {
  getOneTimeWorkspaceLandingRedirect,
  isCompanyManagementNavActive,
  isWorkspaceLandingHandled,
  markWorkspaceLandingHandled,
  WORKSPACE_ENTERED_SESSION_KEY,
} from './MainMenu/utils/mainMenuLanding'
import {
  adminOverviewPaperSx,
  adminOverviewStatusBarSx,
  adminMetricTileSx,
  adminToolCardFullSx,
  adminToolCardIconWrapperSx,
  adminToolDangerCardFullSx,
  adminToolDangerIconWrapperSx,
  outlinedBrandButtonSx,
  subscriptionChipToneFromState,
  subscriptionStatusChipSx,
} from './MainMenu/utils/mainMenuThemeSx'
import { alpha } from '@mui/material/styles'
import { brandHexOrFallback } from '../utils/companyBrandingPalette'
import { trackEvent } from '../services/analyticsService'
import { fetchProductUpdates } from '../services/productUpdatesService'
import { hasUnreadProductUpdates } from '../utils/productUpdatesLastSeen'
import { normalizeRoutePath } from '../utils/routeUtils'

// Styled components
import {
  MenuContainer,
  Backdrop,
  Sidebar,
  ContentPanel,
  LogoContainer,
  MenuList,
  MenuListItem,
  UserProfile,
  UserInfo,
  UserName,
  UserRole,
  SectionTitle,
  RoomGrid,
  RoomCardContainer,
  LoadingContainer,
  NoRoomsMessage,
  PasswordDialog,
  ContentHeader,
  PageTitle,
  SectionDivider,
  NoRooms,
  EmptyStateIcon,
  AdminMenuContainer,
  AdminMenuCard,
  CardIconWrapper,
  AdminRoomCard,
  AdminRoomCardContent,
} from './styles/MainMenu.styles'

// Store and hooks
import { useAppSelector, useAppDispatch } from '../hooks'
import store from '../stores'
import back4appService, { profileService } from '../services/back4app'
import { setAuthenticated, setUserData, setPlayerName } from '../stores/UserStore'
import type { UserData } from '../types/user'
import { setLoadingGame } from '../stores/GameStore'
import { setJoinedRoomData, addResolvedRoom } from '../stores/RoomStore'
import { ServiceFactory } from '../core/factories/ServiceFactory'
import logo from '../images/logo.png'
import svgLogo from '/assets/images/SVGLogo.svg'
import {
  initializePhaserGame,
  getPhaserGameSync,
  isPhaserGameReady,
  preloadPhaserGame
} from '../PhaserGame'
import { phaserEvents, Event } from '../events/EventCenter'

// Bootstrap type for casting - loaded dynamically with Phaser
type Bootstrap = any
import { addNotification } from '../stores/NotificationStore'
import { lobbyService, LobbyStats } from '../services/lobbyService'
import { useVisiblePoll } from '../hooks/useVisiblePoll'
import { useRoutingStore, setupRoutingListeners } from '../stores/routingStore'
import { useJoinStore } from '../stores/joinStore'
import { isServerInviteToken, previewRoomInviteCloud } from '../services/roomInviteService'
import type { InviteLinkGateState } from './PermissionsDialog'
import { resolveRoomForJoin } from '../services/roomResolver'
import { fetchMeetingLinkStatus, meetingLinkRefusal } from '../services/meetingLinkStatus'
import { decryptString } from '../utils/crypto'
import RoutingTest from './RoutingTest'
import { useLobbyStats } from '../hooks/useLobbyStats'
import { useOptimizedRoomList, adminCompanyRooms, type OptimizedRoom } from '../hooks/useOptimizedRoomList'
import ColyseusRoomService from '../services/back4app/ColyseusRoomService'
import MapService from '../services/back4app/MapService'
import { getServerBaseUrl } from '../utils/serverUrl'
import { joinedRoomFields } from '../services/joinedRoomFields'
import { useCompanyPermissions } from '../hooks/useCompanyPermissions'
import { useRbacCompanySync, useSurface } from '../hooks/usePermission'
import { ManageRoomsButton } from './MainMenu/ManageRoomsButton'
import { useSession } from '../contexts/SessionContext'
import { useCompanyDisabledState } from '../hooks/useCompanyDisabledState'
import { useCompanyAccessGate, useCompanyAccessRefresh } from '../hooks/useCompanyAccess'
import {
  isCompanyBlockedHttpCode,
  isCompanyBlockedJoinError,
  signalCompanyAccessRefusal,
} from '../services/CompanyAccessService'
import CompanyDisabledScreen from './common/CompanyDisabledScreen'
import SuspendedCompanyNav from './MainMenu/SuspendedCompanyNav'
import { isAvailableWhenCompanySuspended } from './MainMenu/utils/suspendedCompanyGate'
import { useRoomRefreshManager } from '../hooks/useRoomRefreshManager'
import { useAccountStatus } from '../hooks/useAccountStatus'
import { useDashboardBookingReminders } from '../hooks/useDashboardBookingReminders'
import { CompanyService } from '../services/CompanyService'
import { CompanyRestrictionService } from '../services/CompanyRestrictionService'
import { CompanyDisabledAlert } from './common/CompanyDisabledAlert'
import { RoomAccessGuard } from './common/RoomAccessGuard'
import {
  AccountAccessGuard,
  CustomRoomJoiningGuard,
  DashboardAccessGuard,
  CompanyManagementGuard
} from './common/AccountAccessGuard'
import authService from '../services/authService'
import { isCompanyGatedSection } from '../utils/appRenderGates'
import { getStoredSessionToken } from '../utils/sessionToken'
import { usePermissionStore } from '../stores/permissionStore'
import {
  MAP_EDITOR_JOIN_EVENT,
  clearMapEditorReturn,
  peekMapEditorReturn,
  type MapEditorRoomTarget,
} from '../services/mapEditorRoom'
import { getDisplayName as getPersonDisplayName } from '../utils/personName'

const MAP_EDITOR_RETURN_TIMEOUT_MS = 20_000
// Route guard is now handled by the unified guard evaluator system

// Extend window interface for company lobby handler
declare global {
  interface Window {
    handleCompanyLobbyAfterPermissions?: () => Promise<void>
    networkInstance?: any
  }
}

interface MainMenuProps {
  onJoinPublicLobby: () => void
  useRouting?: boolean
}

enum MenuSection {
  Dashboard = 'publicLobby',
  Rooms = 'myRooms',
  Meetings = 'meetings',
  Profile = 'profile',
  Admin = 'adminMenu',
}

// Add interface definitions for default rooms
interface ServerDefaultRoom {
  roomId: string | null
  name: string
  description?: string
  clients?: number
  isHealthy?: boolean
  locked?: boolean
  elapsedTime?: number
}

interface UIDefaultRoom {
  id: string
  name: string
  description: string
  members: number
  isPublic: boolean
  has_password?: boolean
  isHealthy?: boolean
  mapId?: string
  spawnPoint?: { x: number; y: number }
  metadata?: any
  customRoomId?: string
}

// Define an interface for room type
interface Room {
  id: string
  name: string
  description: string
  members: number
  isPublic: boolean
  creatorName?: string
  mapId?: string
  mapName?: string
}

// Add performance optimization constants
const OPTIMIZATION_CONFIG = {
  // Reduced polling intervals for better performance with 100+ users
  LOBBY_STATS_INTERVAL: 60000, // 60s instead of 30s
  USER_COUNT_INTERVAL: 30000,  // 30s instead of 15s
  ROOM_REFRESH_INTERVAL: 60000, // 60s instead of 30s
  BACK4APP_CACHE_TTL: 120000,  // 2 minutes cache
  DEBOUNCE_DELAY: 2000,        // 2s debounce for manual refreshes
  VIRTUAL_SCROLL_THRESHOLD: 50, // Enable virtual scrolling for 50+ rooms
  MAX_ROOMS_PER_PAGE: 20,      // Pagination for large room lists
}

const checkWorkspacePath = (path : string) : boolean => {
  const WORKSPACE_EXACT = new Set<string>(['/', '/dashboard','/room','/rooms'])
  const WORKSPACE_PREFIXES: string[] = ['/publicLobby', '/company']

  return WORKSPACE_EXACT.has(path) || WORKSPACE_PREFIXES.some((p) => path.startsWith(p))
}

export default function MainMenu({ onJoinPublicLobby, useRouting = false }: MainMenuProps) {
  // OPTIMIZATION: Preload Phaser in background when user enters MainMenu
  // This dramatically reduces time-to-game when they click "Join Room"
  useEffect(() => {
    preloadPhaserGame()
  }, [])

  const { sessionReady: parseSessionReady } = useSession()

  // Function to join any company's lobby as a global admin
  const joinAnyCompanyLobby = async (companyId: string, companyName: string) => {
    try {
      console.log(`[MainMenu] Global admin joining company lobby for: ${companyName} (${companyId})`)

      // Get the company lobby room ID from the server
      // SC-422: only a member of the company (or a platform admin) may open its lobby.
      const response = await fetch(`/api/company-lobby-rooms/${companyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getStoredSessionToken()}`,
        },
      })

      if (!response.ok) {
        // A closed company (SC-409) must not fall back to the public lobby below: re-check
        // access so a member sees the disabled screen, and tell an admin why nothing opened.
        const body = await response.json().catch(() => null)
        if (isCompanyBlockedHttpCode(body?.code)) {
          signalCompanyAccessRefusal()
          store.dispatch(
            addNotification({
              message: `${companyName || 'This company'} is suspended or no longer exists.`,
              type: 'warning',
              duration: 6000,
            })
          )
          return
        }
        throw new Error(`Failed to get company lobby room: ${response.statusText}`)
      }

      const result = await response.json()

      if (!result.success || !result.roomId) {
        throw new Error('Failed to get company lobby room ID')
      }

      console.log(`[MainMenu] Company lobby room ID: ${result.roomId}`)

      // Store the room ID in localStorage for the permissions dialog to use
      localStorage.setItem('pendingRoomId', result.roomId)
      localStorage.setItem('roomHasPassword', 'false') // Company lobbies don't have passwords

      // Set up the room join handler for after permissions are granted
      const handleAnyCompanyLobbyAfterPermissions = async () => {
        try {
          // Initialize Phaser lazily when joining a room
          const phaserGame = await initializePhaserGame()
          const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
          if (bootstrap?.network) {
            // Use direct room joining for company lobby rooms (bypass availableRooms check)
            const success = await (bootstrap.network as any).joinCompanyLobbyById(result.roomId)

            if (success) {
              // Get the room state to extract mapId and customRoomId
              const roomState = bootstrap.network.getRoomState()
              // SC-422: the room's map comes from its ColyseusRoom row; a room without one is bad data.
              const mapId = roomState?.mapId
              if (!mapId) throw new Error('This room has no map')
              const joined = joinedRoomFields(roomState)
              const customRoomId = joined.customRoomId
              const roomName = joined.name || `${companyName} Lobby`
              const description = joined.description || `Private lobby for ${companyName} company members`

              // Convert room state spawn (pixels) to tile coords for Redux - ensures spawn data before launchGame
              const spawnPoint = roomState?.spawnX != null && roomState?.spawnY != null
                ? { x: Math.round(roomState.spawnX / 32), y: Math.round(roomState.spawnY / 32) }
                : undefined

              // Update Redux with joined room data
              dispatch(setJoinedRoomData({
                id: result.roomId,
                name: roomName,
                description: description,
                mapId,
                customRoomId,
                companyId: companyId,
                spawnPoint,
              }))

              console.log(`[MainMenu] Launching company lobby with map ID: ${mapId}, customRoomId: ${customRoomId}`)

              // Launch game with the correct map ID and customRoomId
              bootstrap.launchGame({
                mapId: mapId,
                // The game keys its VR areas by this id, so it needs one even when the state had none.
                customRoomId: customRoomId || result.roomId,
                fromRoomCreation: false,
              })
            } else {
              throw new Error('Failed to join company lobby room')
            }
          } else {
            throw new Error('Bootstrap network not available')
          }
        } catch (error) {
          console.error('[MainMenu] Error joining company lobby after permissions:', error)
          // Fallback to public lobby if company lobby fails
          console.log('[MainMenu] Falling back to public lobby due to company lobby error')
          onJoinPublicLobby()
        }
      }

      // Store the handler for use after permissions are granted
      window.handleCompanyLobbyAfterPermissions = handleAnyCompanyLobbyAfterPermissions

      // Show the permissions dialog
      setShowPermissionsDialog(true)

    } catch (error) {
      console.error('[MainMenu] Error joining company lobby:', error)
      // Fallback to public lobby if company lobby fails
      console.log('[MainMenu] Falling back to public lobby due to company lobby error')
      onJoinPublicLobby()
    }
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [navDrawerOpen, setNavDrawerOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const drawerPaperRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  // Routing integration
  const routingStore = useRoutingStore()

  // Force re-render when routing store changes (critical for browser back button)
  useEffect(() => {
    if (useRouting) {
      console.log('[MainMenu] Routing store changed:', {
        currentPath: routingStore.currentPath,
        currentSection: routingStore.currentSection,
      })
      // This effect ensures the component re-renders when routing store changes
      // Zustand should handle this automatically, but we're being explicit here
    }
  }, [routingStore.currentPath, routingStore.currentSection, useRouting])

  // Use routing store's current section if routing is enabled, otherwise use local state
  const [localActiveSection, setLocalActiveSection] = useState<
    | 'rooms'
    | 'friends'
    | 'profile'
    | 'settings'
    | 'adminMenu'
    | 'adminDashboard'
    | 'adminEnvironmentConfig'
    | 'adminEntityCatalogue'
    | 'adminBillingSettings'
    | 'adminServerJobs'
    | 'adminMaps'
    | 'adminTransactionalEmails'
    | 'adminRoomManagement'
    | 'adminMapEditorPlugins'
    | 'routingTest'
    | 'adminCompanyManagement'
    | 'companyManagement'
    | 'companyOverview'
    | 'companyDashboard'
    | 'companySetup'
    | 'companyBilling'
    | 'companyUsers'
    | 'companySettings'
    | 'companyDialog'
    | 'inviteUserDialog'
    | 'editUserDialog'
    | 'usersDialog'
    | 'companyRoomsDialog'
    | 'publicLobby'
    | 'myRooms'
    | 'adminAllRooms'
    | 'adminTransactionalEmails'
    | 'adminEndpointTesting'
    | 'adminProductUpdates'
  >('publicLobby')

  // Determine which section to use
  const activeSection = useRouting ? routingStore.currentSection : localActiveSection

  const appBarSectionTitle = useMemo(
    () => getMainMenuSectionLabel(activeSection as MainMenuSection),
    [activeSection],
  )
  const navScrollKey = useMemo(
    () => getMainMenuNavScrollSection(activeSection as MainMenuSection),
    [activeSection],
  )

  // Debug logging for active section
  useEffect(() => {
    // console.log(`[MainMenu] Active section changed to: ${activeSection}, useRouting: ${useRouting}`)
  }, [activeSection, useRouting])

  // Enhanced section change handler that supports routing
  const handleSectionChange = useCallback((section: any) => {
    // console.log(`[MainMenu] handleSectionChange called with section: ${section}, useRouting: ${useRouting}`)
    if (useRouting) {
      routingStore.navigateToSection(section)
    } else {
      setLocalActiveSection(section)
    }
    if (isMobile) {
      setNavDrawerOpen(false)
    }
  }, [useRouting, routingStore, isMobile])

  const handleNavDrawerClose = useCallback(() => {
    setNavDrawerOpen(false)
    queueMicrotask(() => menuButtonRef.current?.focus())
  }, [])

  useEffect(() => {
    if (!navDrawerOpen || !isMobile) return
    const t = window.setTimeout(() => {
      const root = drawerPaperRef.current
      if (!root) return
      const sel = navScrollKey
        ? `[data-mainmenu-section="${navScrollKey}"]`
        : '[data-mainmenu-section]'
      const el = root.querySelector(sel) as HTMLElement | null
      el?.scrollIntoView({
        block: 'nearest',
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })
      el?.focus({ preventScroll: true })
    }, 150)
    return () => window.clearTimeout(t)
  }, [navDrawerOpen, isMobile, navScrollKey, prefersReducedMotion])

  // Initialize routing listeners on component mount
  useEffect(() => {
    console.log(`[MainMenu] useEffect called with useRouting: ${useRouting}`)
    if (useRouting) {
      console.log('[MainMenu] Setting up routing listeners')
      setupRoutingListeners()

      // Ensure we sync with URL on mount and when routing is enabled
      const store = useRoutingStore.getState()
      store.syncWithURL()
    }
  }, [useRouting])

  // Subscribe to routing store changes to ensure re-renders on browser navigation
  useEffect(() => {
    if (!useRouting) return

    // Subscribe to routing store changes
    const unsubscribe = useRoutingStore.subscribe(
      (state) => state.currentSection,
      (currentSection) => {
        console.log(`[MainMenu] Routing store currentSection changed to: ${currentSection}`)
        // Force a re-render by updating a dummy state if needed
        // Actually, Zustand should handle this automatically, but we log for debugging
      }
    )

    return () => {
      unsubscribe()
    }
  }, [useRouting])

  const [showProfileDialog, setShowProfileDialog] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [selectedRoomId, setSelectedRoomId] = useState('')
  const [password, setPassword] = useState('')
  const [showPasswordError, setShowPasswordError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const [duplicateLoginConfirmPromise, setDuplicateLoginConfirmPromise] = useState<{ resolve: (value: boolean) => void } | null>(null)
  const [pendingRoomJoin, setPendingRoomJoin] = useState<{
    roomId: string
    password: string | null
    isDefaultRoom: boolean
    defaultRoomData?: any
    companyId?: string | null
    customRoomId?: string
    origin?: 'dashboard' | 'invite_link' | 'reconnection' | 'deep_link' | 'room_creation' | 'map_editor' | 'unknown'
    fromInviteLink?: boolean
    inviteToken?: string | null
    sharerUserIdForNear?: string | null
    defaultJoinNear?: boolean
    joinVirtualRoomId?: string | null
  } | null>(null)

  const [inviteJoinNearHost, setInviteJoinNearHost] = useState(true)
  const [inviteLinkGate, setInviteLinkGate] = useState<InviteLinkGateState>({
    loading: false,
    blocked: false,
  })

  /** Prevents concurrent duplicate runs of the ?rid= deep-link handler (routing + popstate). */
  const joinDeepLinkInFlightRef = useRef(false)

  /**
   * A dashboard room waiting on its password. Password rooms aren't listed, so the join needs
   * the room's customRoomId and company to find it again after the password step.
   */
  const dashboardPasswordJoinRef = useRef<{ roomId: string; customRoomId?: string; companyId: string | null } | null>(null)

  // The Maps admin view opens a map editor room (SC-422); it is joined like any listed room.
  useEffect(() => {
    const onJoinMapEditorRoom = (event: globalThis.Event) => {
      const target = (event as CustomEvent<MapEditorRoomTarget>).detail
      if (!target?.roomId) return
      setPendingRoomJoin({
        roomId: target.roomId,
        password: null,
        isDefaultRoom: false,
        companyId: null,
        customRoomId: target.customRoomId,
        origin: 'map_editor',
      })
      setShowPermissionsDialog(true)
    }
    window.addEventListener(MAP_EDITOR_JOIN_EVENT, onJoinMapEditorRoom)
    return () => window.removeEventListener(MAP_EDITOR_JOIN_EVENT, onJoinMapEditorRoom)
  }, [])

  /** Set when a password-protected join link (?rid=) is opened; merged into pendingRoomJoin after password step. */
  const pendingInviteLinkJoinRef = useRef<{
    roomId: string
    companyId: string | null
    customRoomId?: string
    inviteToken?: string | null
    sharerUserIdForNear?: string | null
    defaultJoinNear?: boolean
    joinVirtualRoomId?: string | null
  } | null>(null)

  // Add pagination state for large room lists
  const [roomPage, setRoomPage] = useState(0)
  const [virtualScrollEnabled, setVirtualScrollEnabled] = useState(false)

  // Add refresh feedback and cooldown state
  const [lastRefreshTime, setLastRefreshTime] = useState(0)
  const [refreshCooldown, setRefreshCooldown] = useState(false)
  const [nextAutoRefreshTime, setNextAutoRefreshTime] = useState(0)
  // Use ref to track the actual next refresh time for stable countdown
  const stableNextRefreshTimeRef = useRef<number>(0)
  const mainMenuViewedDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Add state for collapsible sections
  const [defaultRoomsExpanded, setDefaultRoomsExpanded] = useState(true)

  // Add state for admin all rooms view
  const [adminAllRoomsExpanded, setAdminAllRoomsExpanded] = useState<{ [companyId: string]: boolean }>({})
  const [allCompanies, setAllCompanies] = useState<any[]>([])
  const [loadingAllCompanies, setLoadingAllCompanies] = useState(false)
  /** SC-422: every company's ColyseusRoom rows, for the All Rooms (Admin View) section. */
  const [adminRooms, setAdminRooms] = useState<OptimizedRoom[]>([])
  /** The Rooms page's room management dialog (company admins and managers). */
  const [showManageRoomsDialog, setShowManageRoomsDialog] = useState(false)
  const [companyUsage, setCompanyUsage] = useState<Record<string, { companyId: string | null; totalUsers: number; rooms: any[] }>>({})
  const [productUpdatesUnread, setProductUpdatesUnread] = useState(false)
  const [productUpdatesActiveVoting, setProductUpdatesActiveVoting] = useState(false)

  const userData = useAppSelector((state) => state.user.userData)
  const userRole = useAppSelector((state) => state.user.userRole)
  const dispatch = useAppDispatch()

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return
    if (normalizeRoutePath(window.location.pathname) === '/verify-email') return
    if (!userData?.id || userData.emailVerified !== false) return
    window.location.replace('/verify-email')
  }, [userData?.id, userData?.emailVerified])
  const colyseusRooms = useAppSelector((state) => state.room.availableRooms)
  const lobbyJoined = useAppSelector((state) => state.room.lobbyJoined)
  const roomJoined = useAppSelector((state) => state.room.roomJoined)
  const joinStore = useJoinStore()

  // Use the company permissions hook (moved up to provide currentCompany for useLobbyStats)
  const { hasCompanyAccess, isCompanyManagerOrAdmin, isGlobalAdmin, currentCompany, rbacCompanyId, loading: companyPermissionsLoading, error: companyPermissionsError, refreshPermissions, canAccessCompanyManagement: companyCanAccessCompanyManagement, companyUnknown } = useCompanyPermissions()

  // What the person may do in their company (SC-439): menus, tabs and routes follow it. The
  // server and cloud check every action themselves.
  useRbacCompanySync({
    userId: userData?.id ?? null,
    // A platform admin's own company too, not only a member's (SC-439): see useCompanyPermissions.
    companyId: rbacCompanyId ?? null,
    companyResolved: !companyPermissionsLoading,
    companyUnknown,
  })
  const canManageRooms = useSurface('rooms-page.manage-rooms')

  // Use company disabled state early (needed for useAccountStatus sync effect)
  const companyDisabledState = useCompanyDisabledState()

  // APP_workspace_entered: Fire when user enters their Sevv workspace (dashboard/lobby UI loads)
  useEffect(() => {
    if (!parseSessionReady || !userData?.id || roomJoined || companyPermissionsLoading) return
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    const isWorkspacePath = checkWorkspacePath(path)
    if (!isWorkspacePath) return
    const firedKey = WORKSPACE_ENTERED_SESSION_KEY
    if (sessionStorage.getItem(firedKey)) return
    sessionStorage.setItem(firedKey, '1')
    trackEvent('APP_workspace_entered', {
      workspace_id: currentCompany?.id ?? null,
      workspace_name: currentCompany?.name ?? null,
    })
  }, [parseSessionReady, userData?.id, roomJoined, companyPermissionsLoading, currentCompany?.id, currentCompany?.name])

  // APP_analytics_session_started after ≥30min idle on workspace shell (replaces APP_mainmenu_revisit)
  const ANALYTICS_SESSION_IDLE_MS = 30 * 60 * 1000
  useEffect(() => {
    if (!parseSessionReady || !userData?.id || roomJoined) return
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    const isWorkspacePath = checkWorkspacePath(path)
    if (!isWorkspacePath) return
    const now = Date.now()
    const lastVisitRaw = sessionStorage.getItem('app_last_visit_timestamp')
    if (lastVisitRaw) {
      const lastVisit = parseInt(lastVisitRaw, 10)
      if (!isNaN(lastVisit) && now - lastVisit >= ANALYTICS_SESSION_IDLE_MS) {
        trackEvent(
          'APP_analytics_session_started',
          companyPermissionsLoading
            ? {
                entry_point: 'workspace_shell',
                minutes_since_last_activity: Math.floor((now - lastVisit) / 60000),
              }
            : {
                entry_point: 'workspace_shell',
                minutes_since_last_activity: Math.floor((now - lastVisit) / 60000),
                workspace_id: currentCompany?.id ?? null,
                workspace_name: currentCompany?.name ?? null,
              }
        )
      }
    }
    sessionStorage.setItem('app_last_visit_timestamp', String(now))
  }, [parseSessionReady, userData?.id, roomJoined, companyPermissionsLoading, currentCompany?.id, currentCompany?.name])

  // APP_mainmenu_viewed debounced (~450ms idle on section selection)
  useEffect(() => {
    if (!parseSessionReady || !userData?.id || roomJoined || companyPermissionsLoading) return
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    const isWorkspacePath = checkWorkspacePath(path)
    if (!isWorkspacePath) return
    if (mainMenuViewedDebounceTimerRef.current) {
      clearTimeout(mainMenuViewedDebounceTimerRef.current)
      mainMenuViewedDebounceTimerRef.current = null
    }
    mainMenuViewedDebounceTimerRef.current = setTimeout(() => {
      mainMenuViewedDebounceTimerRef.current = null
      trackEvent('APP_mainmenu_viewed', {
        section: activeSection,
        workspace_id: currentCompany?.id ?? null,
        workspace_name: currentCompany?.name ?? null,
      })
    }, 450)
    return () => {
      if (mainMenuViewedDebounceTimerRef.current) {
        clearTimeout(mainMenuViewedDebounceTimerRef.current)
        mainMenuViewedDebounceTimerRef.current = null
      }
    }
  }, [activeSection, parseSessionReady, userData?.id, roomJoined, companyPermissionsLoading, currentCompany?.id, currentCompany?.name])

  // Product updates badge: fetch when workspace menu is relevant (not in-game)
  useEffect(() => {
    if (!parseSessionReady || !userData?.id || roomJoined) {
      setProductUpdatesUnread(false)
      setProductUpdatesActiveVoting(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetchProductUpdates()
        if (cancelled) return
        const rel = res.payload?.releases ?? []
        setProductUpdatesUnread(hasUnreadProductUpdates(rel))
        setProductUpdatesActiveVoting(res.hasActiveVoting === true)
      } catch {
        if (!cancelled) {
          setProductUpdatesUnread(false)
          setProductUpdatesActiveVoting(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [parseSessionReady, userData?.id, roomJoined])

  useEffect(() => {
    const onSeen = () => setProductUpdatesUnread(false)
    window.addEventListener('productUpdatesDidMarkSeen', onSeen)
    return () => window.removeEventListener('productUpdatesDidMarkSeen', onSeen)
  }, [])

  // Use account status early for lobby user count scope (global vs company)
  const {
    accountStatus,
    loading: accountStatusLoading,
    error: accountStatusError,
    canCreateRooms,
    canJoinRooms,
    canJoinCustomRooms,
    canAccessDashboard,
    canAccessCompanyManagement,
    canAccessBilling,
    canAccessProfile,
    hasFullAccess,
    isGlobalAdmin: accountStatusIsGlobalAdmin,
    subscriptionStatus,
    paymentStatus,
    restrictions,
    statusMessage,
    needsAction,
    actionType,
    actionMessage,
    refresh: refreshAccountStatus,
    checkPermission
  } = useAccountStatus()

  /** Elevated admin UI (admin menu, /admin) — server GET /api/admin/capabilities only. */
  const serverVerifiedElevated = usePermissionStore(
    (s) => s.adminCapabilitiesVerified && (s.isGlobalAdmin || s.isPlatformAdmin),
  )
  /** Billing/lobby bypass: server-verified elevated OR cloud accountStatus (while caps load). */
  const globalAdminLikeForBypass =
    serverVerifiedElevated || accountStatusIsGlobalAdmin

  // Leaving a map editor room comes back to Admin → Maps on that map (SC-422). The admin route
  // guard refuses /admin/maps until the admin check is in, so a loader covers the dashboard until
  // then instead of showing it. AdminMaps selects the map and clears the hint.
  const permissionsReady = usePermissionStore((s) => s.permissionsReady)
  const adminCheckDone = usePermissionStore((s) => s.adminCapabilitiesVerified)
  const [returningToMaps, setReturningToMaps] = useState(() => !!peekMapEditorReturn())
  // The navigation re-renders synchronously (routing store) before setReturningToMaps(false)
  // lands, with a new handleSectionChange, so without this the effect navigates again forever.
  const returnToMapsHandled = useRef(false)
  useEffect(() => {
    if (!returningToMaps || returnToMapsHandled.current || !permissionsReady || !adminCheckDone) return
    returnToMapsHandled.current = true
    if (serverVerifiedElevated) handleSectionChange('adminMaps')
    else clearMapEditorReturn()
    setReturningToMaps(false)
  }, [returningToMaps, permissionsReady, adminCheckDone, serverVerifiedElevated, handleSectionChange])
  useEffect(() => {
    if (!returningToMaps) return
    // A failed admin check never settles; land on the dashboard rather than load forever.
    const timer = window.setTimeout(() => {
      clearMapEditorReturn()
      setReturningToMaps(false)
    }, MAP_EDITOR_RETURN_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [returningToMaps])

  // Suspended-company gate (SC-409), from GET /api/company/me/access. Platform admins are
  // never gated. 'pending' renders as before but holds deep links and the landing redirect.
  useCompanyAccessRefresh(userData?.id ?? null, globalAdminLikeForBypass)
  const companyAccessGate = useCompanyAccessGate(userData?.id ?? null, globalAdminLikeForBypass)
  const companyGated = companyAccessGate.state === 'gated'

  // Meeting reminders while in the main menu, from wherever the user lands. Only once access is
  // open: cloud refuses a suspended company's meetings, and the hook re-reads every few minutes.
  useDashboardBookingReminders(companyAccessGate.state === 'open' ? currentCompany?.id ?? null : null)
  // While /me/access is still in flight, the legacy hooks already flag a suspended company as
  // "disabled" and, for any role, offer My Company (and the tier chip) until the gate answers.
  // Hide those items only in that case, so an active company's managers see no delay.
  // Known admins (globalAdminLikeForBypass) are never gated, so this never hides their Company
  // Management entry. While admin status is still loading, the companyPermissionsLoading guard
  // hides it anyway.
  const hideCompanyItemsUntilGateAnswers =
    companyAccessGate.state === 'pending' &&
    companyDisabledState.isDisabled &&
    !globalAdminLikeForBypass

  const handleJoinPublicLobby = useCallback(async () => {
    if (companyGated) return

    if (companyDisabledState.isDisabled) {
      console.log('[MainMenu] Public lobby join blocked - company is disabled')
      return
    }

    if (!currentCompany?.id) {
      onJoinPublicLobby()
      return
    }

    if (globalAdminLikeForBypass) {
      console.log('[MainMenu] Global admin - bypassing dashboard access permission check')
      onJoinPublicLobby()
      return
    }

    try {
      const permissionResult = await checkPermission('access_dashboard')
      if (!permissionResult.allowed) {
        console.log('[MainMenu] Public lobby join blocked - insufficient permissions:', permissionResult.reason)
        return
      }
    } catch (error) {
      console.error('[MainMenu] Error checking dashboard access permission:', error)
      return
    }

    onJoinPublicLobby()
  }, [
    companyGated,
    companyDisabledState.isDisabled,
    currentCompany?.id,
    globalAdminLikeForBypass,
    checkPermission,
    onJoinPublicLobby,
  ])

  // Optimize profile data loading with better caching
  useEffect(() => {
    const loadProfileData = async () => {
      if (userData?.id && (!userData.firstName || !userData.lastName)) {
        console.log('[MainMenu] Profile data incomplete, loading from service')
        try {
          const profile = await authService.getCurrentProfile()
          if (profile) {
            console.log('[MainMenu] Profile data loaded and updated:', profile)
            // Update Redux store with complete profile data
            const updatedUserData: UserData = {
              ...userData,
              firstName: profile.firstName,
              lastName: profile.lastName,
              displayName: profile.displayName,
              title: profile.title,
              contact: profile.contact,
              bio: profile.bio,
              avatar: profile.avatar,
              gameCharacter: profile.gameCharacter,
              customAvatarUrl: profile.customAvatarUrl,
              profileCompleted: profile.profileCompleted,
            }
            dispatch(setUserData(updatedUserData))

            // Update player name mapping for immediate UI updates (only if in a room session)
            const displayName = getPersonDisplayName({ ...profile, username: userData.username })
            const sessionId = store.getState().user.sessionId
            if (sessionId) {
              dispatch(setPlayerName({ id: sessionId, name: displayName }))
            }

            // Player name service removed - using centralized nameplate system instead

            console.log('[MainMenu] Profile data propagated, display name:', displayName)
          }
        } catch (error) {
          console.error('[MainMenu] Error loading profile data:', error)
        }
      }
    }

    loadProfileData()
  }, [userData?.id, dispatch])

  // Use the new lobby stats hook with optimized intervals
  const {
    isLoading: isLoadingLobbyStats,
    isRefreshing,
    refreshStats: refreshLobbyStats,
    refreshUserCount,
    getUserCount,
  } = useLobbyStats(
    activeSection === 'publicLobby' && !roomJoined && !companyGated,
    // Global admins see total users; company users see only their company's users
    (globalAdminLikeForBypass) ? undefined : currentCompany?.id
  )

  const [logoLoaded, setLogoLoaded] = useState(false)
  const settings = useAppSelector((state) => state.workspace.settings)
  // Registration Mode Closed is the go-to-market waitlist: nobody starts a new company on
  // their own (cloud refuses selfServeCreateCompany too). Unset or not loaded means open.
  // [SC-443]
  const isRegistrationClosed = settings?.registration_enabled === false

  // Add platform branding state
  const platformName = settings?.platform_name || 'Sevv Industries'
  const platformUrl = settings?.platform_url || 'https://sevv.industries'
  const logoUrlState = settings?.logo_url || logo
  const primaryColor = theme.palette.primary.main
  const displayPrimaryColor = brandHexOrFallback(currentCompany?.primary_color, primaryColor)


  // Add state for map information lookup
  const [mapDefinitions, setMapDefinitions] = useState<{
    [key: string]: { id: string; name: string; description: string }
  }>({})

  // Helper: Debug flag for map/logging
  const isDebugMapName =
    process.env.NODE_ENV !== 'production' && localStorage.getItem('DEBUG_MAP_NAME') === 'true'
  const isDebugRoomInfo =
    process.env.NODE_ENV !== 'production' && localStorage.getItem('DEBUG_ROOM_INFO') === 'true'

  // Simple admin debug function (development only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      (window as any).debugAdmin = {
        forceAdmin: () => {
          localStorage.setItem('forceAdmin', 'true')
          window.location.reload()
        },
        removeAdmin: () => {
          localStorage.removeItem('forceAdmin')
          window.location.reload()
        }
      }
    }
  }, [])

  // Memoize getMapName to avoid recomputation
  const getMapName = useCallback(
    (mapId: string): string => {
      if (!mapId) return 'Default Map'
      if (isDebugMapName) {
        // console.log(`Getting map name for mapId: ${mapId}`, {
        //   availableMaps: Object.keys(mapDefinitions),
        //   hasMapInDefinitions: !!mapDefinitions[mapId],
        // })
      }
      if (mapDefinitions[mapId]) {
        return mapDefinitions[mapId].name
      }
      const commonMapNames: { [key: string]: string } = {
        main: 'Main Office',
        conference: 'Conference Room',
        office: 'Engineering Office',
        lobby: 'Conference Lobby',
        design: 'Design Studio',
        sales: 'Sales Floor',
        hr: 'HR Department',
        leadership: 'Executive Suite',
        finance: 'Finance Department',
        product: 'Product Lab',
        support: 'Support Center',
      }
      if (commonMapNames[mapId]) {
        return commonMapNames[mapId]
      }
      return mapId.charAt(0).toUpperCase() + mapId.slice(1).replace(/_/g, ' ')
    },
    [mapDefinitions]
  )

  // Use the optimized room list hook with reduced polling
  const {
    rooms: optimizedRooms,
    loading: roomsLoading,
    refresh: refreshRoomsFromHook,
  } = useOptimizedRoomList(
    (activeSection === 'myRooms' || activeSection === 'publicLobby') && !roomJoined && !companyGated,
    currentCompany?.id ?? null
  )

  // Memoize room filtering and mapping operations
  // Maps with a reception, for the room cards' bell (SC-422: the reception belongs to the map).
  const [receptionMapIds, setReceptionMapIds] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    if (!userData?.id) return
    let cancelled = false
    MapService.listEnabled()
      .then((maps) => {
        if (!cancelled) setReceptionMapIds(new Set((maps || []).filter((m) => m.hasReception).map((m) => m.mapId)))
      })
      .catch(() => {
        // The bell is a hint; without the map list the cards just don't show it.
      })
    return () => {
      cancelled = true
    }
  }, [userData?.id])

  const toListedRoom = useCallback(
    (room: OptimizedRoom) => {
      const mapId = room.mapId || room.metadata?.mapId
      return {
        ...room,
        members: room.clients ?? 0,
        isPublic: room.isPublic ?? true,
        name: room.name ?? room.metadata?.name ?? 'Unnamed Room',
        description: room.description ?? room.metadata?.description ?? '',
        mapId: mapId,
        mapName: getMapName(mapId),
        customRoomId: room.metadata?.customRoomId,
        hasReception: !!mapId && receptionMapIds.has(mapId),
      }
    },
    [getMapName, receptionMapIds]
  )
  const byLobbyThenPriority = (a: any, b: any) => {
    const aIsLobby = a.metadata?.isLobby ? 0 : 1
    const bIsLobby = b.metadata?.isLobby ? 0 : 1
    if (aIsLobby !== bIsLobby) return aIsLobby - bIsLobby
    return (a.metadata?.priority ?? 1) - (b.metadata?.priority ?? 1)
  }

  const mappedDefaultRooms = useMemo(() => {
    return optimizedRooms
      // SC-422: company rooms (and the Public Lobby, listed separately). Migrated lobby ids can
      // contain "lobby", so the Public Lobby is excluded by its id, not by a substring.
      .filter((room) => room.metadata?.isDefaultRoom === true && room.metadata?.customRoomId !== 'lobby' && room.name !== 'Public Lobby')
      .map(toListedRoom)
      .sort(byLobbyThenPriority)
  }, [optimizedRooms, toListedRoom])

  const mappedAdminRooms = useMemo(() => adminRooms.map(toListedRoom).sort(byLobbyThenPriority), [adminRooms, toListedRoom])

  // Group every company's rooms by company for the admin view
  const roomsByCompany = useMemo(() => {
    const grouped: { [companyId: string]: { company: any; rooms: any[] } } = {}

    mappedAdminRooms.forEach((room) => {
      const companyId = (room as any).metadata?.companyId
      if (!companyId) return
      if (!grouped[companyId]) {
        // Try to find company name from allCompanies
        const company = allCompanies.find(c => c.id === companyId)
        grouped[companyId] = {
          company: {
            name: company?.name || `Company ${companyId}`,
            id: companyId
          },
          rooms: []
        }
      }
      grouped[companyId].rooms.push(room)
    })

    return grouped
  }, [mappedAdminRooms, allCompanies])

  // SC-422: every room is a company room (ColyseusRoom); the lobby is listed first.
  const roomsCompanyId = currentCompany?.id ?? userData?.companyId ?? null
  const companyRooms = useMemo(
    () => mappedDefaultRooms.filter((room: any) => !!roomsCompanyId && room?.metadata?.companyId === roomsCompanyId),
    [mappedDefaultRooms, roomsCompanyId]
  )
  const companyRoomNames = useMemo(
    () => Object.fromEntries(companyRooms.filter((r) => r.customRoomId).map((r) => [r.customRoomId as string, r.name])),
    [companyRooms]
  )

  // Optimize custom rooms filtering with memoization
  const customRooms = useMemo(() => {
    // Skip detailed debug logging in production
    const isDebugMode =
      process.env.NODE_ENV !== 'production' &&
      localStorage.getItem('DEBUG_ROOM_FILTERING') === 'true'

    if (isDebugMode) {
      // console.log('Filtering custom rooms from available rooms:', colyseusRooms)
    }

    // Filter rooms without excessive logging
    const rooms = colyseusRooms.filter((room) => {
      const isDefaultRoom = room.metadata?.isDefaultRoom === true
      const isCustomRoomType =
        (room.roomId && room.roomId.includes(RoomType.CUSTOM)) ||
        room.metadata?.roomType === RoomType.CUSTOM ||
        room.name === RoomType.CUSTOM

      const includeInCustomRooms = isCustomRoomType && !isDefaultRoom

      // Company-based filtering: only show rooms from the same company
      const roomCompanyId = room.metadata?.companyId || null
      const userCompanyId = currentCompany?.id || null

      // If user has no company, only show rooms with no company
      // If user has a company, only show rooms from the same company
      const companyMatches = userCompanyId
        ? roomCompanyId === userCompanyId
        : false

      const shouldInclude = includeInCustomRooms && companyMatches

      // Only log details in debug mode
      if (isDebugMode) {
        // console.log(`Room ${room.roomId}:`, {
        //   metadata: room.metadata,
        //   name: room.name,
        //   isDefaultRoom,
        //   isCustomRoomType,
        //   includeInCustomRooms,
        //   roomCompanyId,
        //   userCompanyId,
        //   companyMatches,
        //   shouldInclude,
        // })
      }

      return shouldInclude
    })

    if (isDebugMode) {
      // console.log('Filtered custom rooms:', rooms)
    }

    return rooms
  }, [colyseusRooms, currentCompany?.id])

  // Memoize mapped custom rooms for rendering
  const mappedCustomRoomsMemo = useMemo(() => {
    return customRooms.map((room) => {
      const { roomId, metadata, clients } = room
      const mapId = metadata?.mapId
      const mapName = getMapName(mapId)
      if (isDebugRoomInfo) {
        // console.log(`Custom room "${metadata?.name}" map info:`, {
        //   roomId,
        //   mapId,
        //   computedMapName: mapName,
        //   hasMetadata: !!metadata,
        //   metadataMapId: metadata?.mapId,
        // })
      }
      return {
        id: roomId,
        name: metadata?.name || 'Unnamed Room',
        description: metadata?.description || '',
        members: clients || 0,
        isPublic: !(metadata?.hasPassword || false),
        creatorName: metadata?.creatorName || 'Unknown',
        mapId,
        mapName,
      }
    })
  }, [customRooms, getMapName, isDebugRoomInfo])

  // All lobby functionality is now handled by the useLobbyStats hook

  // Reduce logging verbosity in room debug logging
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && colyseusRooms.length > 0) {
      // Just log the count for normal operation
      // console.log(`Available Colyseus rooms: ${colyseusRooms.length}`)

      // Only do detailed logging if explicitly enabled
      if (localStorage.getItem('DEBUG_ROOM_DETAILS') === 'true') {
        // console.log('Available Colyseus rooms:', colyseusRooms)
        // Log each room's metadata to debug
        colyseusRooms.forEach((room) => {
          // console.log(`Room ${room.roomId} metadata:`, room.metadata)
        })
      }
    }
  }, [colyseusRooms])

  // Create an empty myRooms array with the right type
  const myRooms: Room[] = []

  // Check if virtual scrolling should be enabled
  useEffect(() => {
    // Use mappedDefaultRooms.length instead of separate counts to avoid dependency issues
    setVirtualScrollEnabled(mappedDefaultRooms.length > OPTIMIZATION_CONFIG.VIRTUAL_SCROLL_THRESHOLD)
  }, [mappedDefaultRooms.length])

  // Reset pagination when section changes
  useEffect(() => {
    setRoomPage(0)
  }, [activeSection])

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)

  const handleLogout = async () => {
    setLogoutConfirmOpen(false)
    try {
      console.log('[MainMenu] Starting logout process')
      await authService.signOut()
      dispatch(setUserData(null))
      dispatch(setAuthenticated(false))
      console.log('[MainMenu] Logout completed successfully')
    } catch (error) {
      console.error('[MainMenu] Logout error:', error)
      // Fallback to old service if new one fails
      try {
        const fallbackAuthService = (await import('../services/authService')).default
        await fallbackAuthService.signOut()
        dispatch(setUserData(null))
        dispatch(setAuthenticated(false))
      } catch (fallbackError) {
        console.error('[MainMenu] Fallback logout also failed:', fallbackError)
        // Force logout by clearing Redux state
        dispatch(setUserData(null))
        dispatch(setAuthenticated(false))
      }
    }
  }

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    setShowPasswordError(false)

    try {
      if (!lobbyJoined) {
        throw new Error('Not connected to server')
      }

      // Initialize Phaser lazily and get Bootstrap scene
      const phaserGame = await initializePhaserGame()
      const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
      if (!bootstrap || !bootstrap.network) {
        throw new Error('Game not initialized')
      }

      // Validate the password before proceeding
      const validationResult = await bootstrap.network.validateRoomPassword(
        selectedRoomId,
        password
      )

      const inviteCtx = pendingInviteLinkJoinRef.current
      const inviteFields =
        inviteCtx && inviteCtx.roomId === selectedRoomId
          ? {
              companyId: inviteCtx.companyId,
              customRoomId: inviteCtx.customRoomId,
              fromInviteLink: true as const,
              origin: (inviteCtx.joinVirtualRoomId ? 'deep_link' : 'invite_link') as 'deep_link' | 'invite_link',
              inviteToken: inviteCtx.inviteToken ?? null,
              sharerUserIdForNear: inviteCtx.sharerUserIdForNear ?? null,
              defaultJoinNear: inviteCtx.defaultJoinNear ?? true,
              joinVirtualRoomId: inviteCtx.joinVirtualRoomId ?? null,
            }
          : dashboardPasswordJoinRef.current?.roomId === selectedRoomId
            ? {
                companyId: dashboardPasswordJoinRef.current.companyId,
                customRoomId: dashboardPasswordJoinRef.current.customRoomId,
                origin: 'dashboard' as const,
              }
            : {}

      if (validationResult.valid === true) {
        // Password is valid, store it and show permissions dialog
        // console.log('Password validated successfully, proceeding to permissions dialog')
        if (inviteCtx && inviteCtx.roomId === selectedRoomId) {
          pendingInviteLinkJoinRef.current = null
        }
        try {
          localStorage.setItem('pendingRoomPassword', password)
        } catch {
          /* ignore */
        }
        setPendingRoomJoin({
          roomId: selectedRoomId,
          password: password,
          isDefaultRoom: false,
          ...inviteFields,
        })
        setInviteJoinNearHost(inviteFields.defaultJoinNear ?? true)
        resetPasswordDialog()
        setShowPermissionsDialog(true)
      } else if (validationResult.valid === 'unknown' && validationResult.requiresJoin) {
        // For some rooms (non-default), we can't validate directly, so trust and proceed
        // console.log('Room requires direct join for validation, proceeding to permissions dialog')
        if (inviteCtx && inviteCtx.roomId === selectedRoomId) {
          pendingInviteLinkJoinRef.current = null
        }
        try {
          localStorage.setItem('pendingRoomPassword', password)
        } catch {
          /* ignore */
        }
        setPendingRoomJoin({
          roomId: selectedRoomId,
          password: password,
          isDefaultRoom: false,
          ...inviteFields,
        })
        setInviteJoinNearHost(inviteFields.defaultJoinNear ?? true)
        resetPasswordDialog()
        setShowPermissionsDialog(true)
      } else {
        // Password validation failed
        // console.error('Invalid password:', validationResult.message)
        setShowPasswordError(true)
        store.dispatch(
          addNotification({
            message: 'Invalid password',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch (error: any) {
      // console.error('Failed to validate room:', error)
      setShowPasswordError(true)
      store.dispatch(
        addNotification({
          message: error.message || 'Error validating password',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handlePermissionsGranted = async (opts?: { joinNearHost?: boolean }) => {
    // This function is called when the user clicks "Join" in the PermissionsDialog
    // For link-based joins (?rid= parameter), pendingRoomJoin.roomId contains the room ID from the encrypted token
    // The Join button in the permissions dialog will use this roomId to join the correct room

    if (companyGated) {
      setShowPermissionsDialog(false)
      setPendingRoomJoin(null)
      return
    }

    // Check if there's a company lobby handler first
    if (window.handleCompanyLobbyAfterPermissions) {
      console.log('[MainMenu] Using company lobby handler after permissions granted')
      try {
        await window.handleCompanyLobbyAfterPermissions()
        // Clean up the handler after use
        window.handleCompanyLobbyAfterPermissions = null
        setShowPermissionsDialog(false)
        return
      } catch (error) {
        console.error('[MainMenu] Error in company lobby handler:', error)
        // Clean up the handler on error
        window.handleCompanyLobbyAfterPermissions = null
        // Fall back to regular room join logic
      }
    }

    if (!pendingRoomJoin) {
      // console.warn('Permissions granted but no pending room join request')
      return
    }

    // console.log(`Permissions granted, proceeding with room join:`, pendingRoomJoin)

    try {
      // Initialize Phaser lazily when joining a room
      const phaserGame = await initializePhaserGame()
      const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
      if (!bootstrap || !bootstrap.network) {
        throw new Error('Game not initialized')
      }

      setIsLoading(true)
      // Keep loading screen visible during transition
      dispatch(setLoadingGame(true))

      // Handle default room specifically - delegates to our specialized handler
      if (pendingRoomJoin.isDefaultRoom && pendingRoomJoin.defaultRoomData) {
        // console.log(`Processing default room join for "${pendingRoomJoin.defaultRoomData.name}"`)
        await handleDefaultRoomAfterPermissions(pendingRoomJoin.defaultRoomData, pendingRoomJoin.companyId)
      } else {
        // Handle regular room join with password if provided
        // console.log(`Processing regular room join for ID: ${pendingRoomJoin.roomId}`)
        // CRITICAL: Pass company context and origin for strict isolation
        const origin = pendingRoomJoin.origin || (pendingRoomJoin.fromInviteLink ? 'invite_link' : 'dashboard')
        const joinNearHostChoice =
          opts?.joinNearHost !== undefined ? opts.joinNearHost : inviteJoinNearHost
        const joinNearUserId =
          joinNearHostChoice && pendingRoomJoin.sharerUserIdForNear
            ? pendingRoomJoin.sharerUserIdForNear
            : null
        const success = await bootstrap.network.joinCustomById(
          pendingRoomJoin.roomId,
          pendingRoomJoin.password,
          {
            // Editor rooms have no company; the admin's own company must not be sent along.
            companyId:
              pendingRoomJoin.origin === 'map_editor'
                ? null
                : pendingRoomJoin.companyId || currentCompany?.id || null,
            origin: origin,
            customRoomId: pendingRoomJoin.customRoomId,
            inviteToken: pendingRoomJoin.inviteToken ?? null,
            joinNearUserId,
            joinVirtualRoomId: pendingRoomJoin.joinVirtualRoomId ?? null,
          }
        )

        if (success) {
          // Get the room state to extract mapId and customRoomId if available
          const roomState = bootstrap.network.getRoomState()
          const mapId = roomState?.mapId
          if (!mapId) throw new Error('This room has no map')
          const joined = joinedRoomFields(roomState)
          const customRoomId = joined.customRoomId || pendingRoomJoin.customRoomId || 'default'
          const joinedId = (bootstrap.network as any)?.myRoomId || pendingRoomJoin.roomId
          const roomName = joined.name || 'Room'
          const description = joined.description || ''
          // Undefined (not null) when unknown, so the company SEND_ROOM_DATA stored is kept.
          const companyId = joined.companyId

          // Update Redux with joined room data for downstream consumers
          dispatch(setJoinedRoomData({
            id: joinedId,
            name: roomName,
            description: description,
            mapId,
            customRoomId,
            companyId: companyId,
          }))
          // console.log(`Launching game with map ID from room state: ${mapId}, customRoomId: ${customRoomId}`)

          // Launch game with the correct map ID and customRoomId
          bootstrap.launchGame({
            mapId: mapId,
            customRoomId: customRoomId,
            fromRoomCreation: false,
          })

          // Close the dialog only after launching the game
          setShowPermissionsDialog(false)
          setPendingRoomJoin(null)
        } else {
          throw new Error('Failed to join room')
        }
      }
    } catch (error) {
      // console.error('Error in handlePermissionsGranted:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : error && typeof error === 'object' && 'message' in error
            ? String((error as { message: unknown }).message)
            : 'Failed to join room'
      const errorCode =
        error && typeof error === 'object' && 'code' in error ? (error as { code: unknown }).code : undefined

      // The company is closed (SC-409): RoomHandler has already asked for an access re-check,
      // which brings up the disabled screen. Asking for a password again would be misleading.
      if (isCompanyBlockedJoinError(error)) {
        setIsLoading(false)
        dispatch(setLoadingGame(false))
        setShowPermissionsDialog(false)
        setPendingRoomJoin(null)
        store.dispatch(addNotification({ message: errorMessage, type: 'warning', duration: 6000 }))
        return
      }

      const isPasswordError =
        errorMessage.toLowerCase().includes('password') ||
        errorMessage.toLowerCase().includes('invalid password') ||
        errorMessage.includes('403') ||
        errorMessage.includes('503') ||
        errorMessage.toLowerCase().includes('not available') ||
        errorCode === 403 ||
        errorCode === 503

      setIsLoading(false)
      dispatch(setLoadingGame(false))
      setShowPermissionsDialog(false)

      if (isPasswordError && pendingRoomJoin) {
        // Re-open the password dialog with an error so the user can retry.
        // If this was a link-based join, restore invite context so the next
        // successful submission still carries companyId / customRoomId.
        if (pendingRoomJoin.fromInviteLink) {
          pendingInviteLinkJoinRef.current = {
            roomId: pendingRoomJoin.roomId,
            companyId: pendingRoomJoin.companyId ?? null,
            customRoomId: pendingRoomJoin.customRoomId,
            inviteToken: pendingRoomJoin.inviteToken ?? null,
            sharerUserIdForNear: pendingRoomJoin.sharerUserIdForNear ?? null,
            defaultJoinNear: pendingRoomJoin.defaultJoinNear ?? true,
          }
        }
        try {
          localStorage.removeItem('pendingRoomPassword')
        } catch {
          /* ignore */
        }
        setSelectedRoomId(pendingRoomJoin.roomId)
        setShowPasswordError(true)
        setPassword('')
        setShowPasswordDialog(true)
      } else {
        store.dispatch(
          addNotification({
            message: errorMessage,
            type: 'error',
            duration: 5000,
          })
        )
      }

      setPendingRoomJoin(null)
    }
  }

  const handleJoinRoom = async (roomId: string, overrideCompanyId?: string | null) => {
    const effectiveCompanyId = overrideCompanyId || currentCompany?.id || null
    console.log(`[MainMenu] handleJoinRoom called with roomId: ${roomId}, effectiveCompanyId: ${effectiveCompanyId}`)

    if (companyGated) return

    // Prevent joining rooms if company is disabled
    if (companyDisabledState.isDisabled) {
      console.log('[MainMenu] Room join blocked - company is disabled')
      return
    }

    // Global admins bypass all permission checks
    if (globalAdminLikeForBypass) {
      console.log('[MainMenu] Global admin - bypassing room join permission check')
      // Continue with room join logic below
    } else {
      // SC-422: every room is a company room, so one permission covers joining any of them.
      try {
        const permissionResult = await checkPermission('join_room')
        if (!permissionResult.allowed) {
          console.log('[MainMenu] Room join blocked - insufficient permissions:', permissionResult.reason)
          return
        }
      } catch (error) {
        console.error('[MainMenu] Error checking room join permission:', error)
        return
      }
    }

    // SC-422: rooms in the list are the company's ColyseusRoom rows, and an on-demand one may not
    // be running yet. Resolving it by customRoomId starts it on the server and gives the live
    // room id, which the password check and the join both need.
    const listedRoom =
      mappedDefaultRooms.find((room) => room.id === roomId) ?? mappedAdminRooms.find((room) => room.id === roomId)
    let targetRoomId = roomId
    let hasPassword = false
    if (listedRoom?.customRoomId) {
      const roomCompanyId = (listedRoom as any).metadata?.companyId ?? effectiveCompanyId
      const resolved = await resolveRoomForJoin({ customRoomId: listedRoom.customRoomId, companyId: roomCompanyId }, roomCompanyId)
      if (!resolved) {
        store.dispatch(addNotification({ message: `"${listedRoom.name}" is not available right now`, type: 'error', duration: 5000 }))
        return
      }
      targetRoomId = resolved.roomId
      hasPassword = !!(resolved.metadata?.hasPassword || (listedRoom as any).metadata?.hasPassword)
    } else {
      // CRITICAL: Always filter by company ID to prevent joining wrong company's rooms
      const colyseusRoom = colyseusRooms.find((room) => {
        if (room.roomId !== roomId) return false
        if (effectiveCompanyId) return room.metadata?.companyId === effectiveCompanyId
        return globalAdminLikeForBypass
      })
      hasPassword = !!(colyseusRoom?.metadata?.hasPassword || colyseusRoom?.metadata?.isPrivate || colyseusRoom?.metadata?.passwordProtected)
    }

    if (hasPassword) {
      dashboardPasswordJoinRef.current = {
        roomId: targetRoomId,
        customRoomId: listedRoom?.customRoomId,
        companyId: (listedRoom as any)?.metadata?.companyId ?? effectiveCompanyId,
      }
      setSelectedRoomId(targetRoomId)
      setShowPasswordDialog(true)
      return
    }
    setPendingRoomJoin({
      roomId: targetRoomId,
      password: null,
      isDefaultRoom: false,
      companyId: effectiveCompanyId,
      origin: 'dashboard',
      customRoomId: listedRoom?.customRoomId,
    })
    setShowPermissionsDialog(true)
  }

  const handleDefaultRoomAfterPermissions = async (defaultRoomData: any, targetCompanyId?: string | null) => {
    const effectiveCompanyId = targetCompanyId || currentCompany?.id || null
    try {
      console.log(`Handling default room "${defaultRoomData.name}" - attempting to join existing room (effectiveCompanyId: ${effectiveCompanyId})`)
      setIsLoading(true)
      dispatch(setLoadingGame(true))

      // Initialize Phaser lazily when joining a room
      const phaserGame = await initializePhaserGame()
      const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
      if (!bootstrap || !bootstrap.network) {
        throw new Error('Game not initialized')
      }

      // SC-422: every room is a ColyseusRoom row found by its customRoomId. The server starts it
      // if it isn't running; browsers never create rooms.
      const customRoomId = defaultRoomData.customRoomId || defaultRoomData.metadata?.customRoomId
      if (!customRoomId) throw new Error(`Room "${defaultRoomData.name}" has no room id`)
      const resolved = await resolveRoomForJoin({ customRoomId, companyId: effectiveCompanyId }, effectiveCompanyId)
      if (!resolved) throw new Error(`Room "${defaultRoomData.name}" is not available`)

      const success = await bootstrap.network.joinCustomById(resolved.roomId, null, {
        companyId: resolved.companyId ?? effectiveCompanyId,
        origin: 'dashboard',
      })
      if (!success) throw new Error(`Failed to join room "${defaultRoomData.name}"`)

      const mapId = (resolved.metadata?.mapId as string | undefined) || defaultRoomData.mapId
      if (!mapId) throw new Error(`Room "${defaultRoomData.name}" has no map`)
      bootstrap.launchGame({ mapId, customRoomId, fromRoomCreation: false })

      setShowPermissionsDialog(false)
      setPendingRoomJoin(null)
    } catch (error) {
      console.error('Error handling default room:', error)
      store.dispatch(
        addNotification({
          message: error instanceof Error ? error.message : 'Failed to join default room',
          type: 'error',
          duration: 5000,
        })
      )
      setIsLoading(false)
      dispatch(setLoadingGame(false))
      setShowPermissionsDialog(false)
      setPendingRoomJoin(null)
    }
  }

  const resetPasswordDialog = () => {
    if (pendingInviteLinkJoinRef.current) {
      pendingInviteLinkJoinRef.current = null
      try {
        localStorage.removeItem('pendingRoomId')
        localStorage.removeItem('roomHasPassword')
        localStorage.removeItem('pendingRoomPassword')
      } catch {
        /* ignore */
      }
    }
    setShowPasswordDialog(false)
    setPassword('')
    setShowPasswordError(false)
  }

  // Remove the auto-refresh intervals since useOptimizedRoomList handles its own refresh cycle
  // This prevents multiple refresh mechanisms from running simultaneously
  useEffect(() => {
    if (lobbyJoined && !roomJoined) {
      // console.log('MainMenu: Lobby joined, rooms will be refreshed by useOptimizedRoomList hook')

      // Initialize next auto refresh time
      const now = Date.now()
      const nextRefresh = now + OPTIMIZATION_CONFIG.ROOM_REFRESH_INTERVAL
      setNextAutoRefreshTime(nextRefresh)
      // Set stable ref for countdown calculation
      stableNextRefreshTimeRef.current = nextRefresh
    }
  }, [lobbyJoined, roomJoined])

  // Update refresh feedback countdown once per second (countdown displays whole seconds)
  const [countdownTick, setCountdownTick] = useState(0)
  useEffect(() => {
    if (!lobbyJoined || roomJoined) return

    const interval = setInterval(() => {
      setCountdownTick(t => t + 1)
    }, 1000) // 1s is sufficient since countdown shows whole seconds

    return () => clearInterval(interval)
  }, [lobbyJoined, roomJoined])

  // Auto-refresh user count when in public lobby section. Each call is an authenticated request
  // that costs Back4App reads, so the poll follows the shared rule (2 min or slower, not while the
  // tab is hidden or the last call runs, backing off after a failure). Manual refresh is the
  // way to get it sooner.
  const userCountPollState = useRef({ refreshCooldown, refreshUserCount })
  userCountPollState.current = { refreshCooldown, refreshUserCount }
  const userCountPollEnabled =
    lobbyJoined && activeSection === 'publicLobby' && !roomJoined && !companyDisabledState.isDisabled

  useEffect(() => {
    if (!userCountPollEnabled || userCountPollState.current.refreshCooldown) return
    userCountPollState.current.refreshUserCount().catch(console.error)
  }, [userCountPollEnabled])

  useVisiblePoll(async () => {
    const { refreshCooldown: inCooldown, refreshUserCount: refresh } = userCountPollState.current
    if (inCooldown) return true
    await refresh()
    return !lobbyService.didLastUserCountFail()
  }, userCountPollEnabled)

  // Listen for auto-refresh events from useOptimizedRoomList
  useEffect(() => {
    if (roomJoined) return // Don't listen for refresh events when in a room

    const handleRoomsRefreshed = (event: CustomEvent) => {
      if (event.detail?.source === 'auto') {
        // Update last refresh time for auto-refreshes
        const timestamp = event.detail.timestamp
        setLastRefreshTime(timestamp)
        // Update next auto refresh time
        const nextRefresh = timestamp + OPTIMIZATION_CONFIG.ROOM_REFRESH_INTERVAL
        setNextAutoRefreshTime(nextRefresh)
        // Update stable ref for countdown calculation
        stableNextRefreshTimeRef.current = nextRefresh
      }
    }

    const handleCheckRefreshCooldown = (event: CustomEvent) => {
      // Respond to cooldown check requests from useOptimizedRoomList
      const callback = event.detail?.callback
      if (typeof callback === 'function') {
        callback(refreshCooldown)
      }
    }

    window.addEventListener('roomsRefreshed', handleRoomsRefreshed as EventListener)
    window.addEventListener('checkRefreshCooldown', handleCheckRefreshCooldown as EventListener)

    return () => {
      window.removeEventListener('roomsRefreshed', handleRoomsRefreshed as EventListener)
      window.removeEventListener('checkRefreshCooldown', handleCheckRefreshCooldown as EventListener)
    }
  }, [refreshCooldown, roomJoined])

  // Deep link: server invite (?rid=inv_…) or legacy encrypted token — re-runs on route changes and popstate (SPA).
  const processJoinDeepLinkFromUrl = useCallback(async () => {
    if (joinDeepLinkInFlightRef.current) return
    if (roomJoined) return
    if (showPermissionsDialog || showPasswordDialog) return
    // SC-409: a suspended member's booking or invite link resolves nothing, opens no dialog
    // and lands on the disabled screen. While access is still unknown, wait: this callback
    // re-runs when the gate settles. Nothing saves the link meanwhile: it only survives in the
    // URL, and reactivation (`companyAccessReactivation.restore`) reloads that same URL.
    if (companyAccessGate.state !== 'open') return

    const params = new URLSearchParams(window.location.search)

    // ── Booking deep link: /join?room=<customRoomId>&vr=<virtualRoomId>[&booking=<bookingId>] ──
    const dlRoom = params.get('room')
    const dlVr = params.get('vr')
    if (dlRoom && dlVr) {
      const userCompanyId = store.getState().user.userData?.companyId ?? currentCompany?.id ?? null
      if (!userCompanyId) return // Wait for auth — effect re-fires when currentCompany?.id changes
      joinDeepLinkInFlightRef.current = true
      try {
        // Links sent before the booking id was added have none and open the room as before.
        const dlBooking = params.get('booking')
        if (dlBooking) {
          const refusal = meetingLinkRefusal(await fetchMeetingLinkStatus(dlBooking))
          if (refusal) {
            window.history.replaceState({}, '', '/room')
            store.dispatch(addNotification({ message: refusal, type: 'warning', duration: 8000 }))
            return
          }
        }

        const resolved = await resolveRoomForJoin({ customRoomId: dlRoom, companyId: userCompanyId }, userCompanyId)
        if (!resolved) {
          window.history.replaceState({}, '', '/room')
          store.dispatch(
            addNotification({ message: 'Room not found. The office may be offline.', type: 'error', duration: 6000 })
          )
          return
        }

        store.dispatch(
          addResolvedRoom({
            roomId: resolved.roomId,
            name: resolved.name,
            companyId: resolved.companyId,
            customRoomId: resolved.customRoomId,
            metadata: resolved.metadata,
          })
        )

        localStorage.setItem('pendingRoomId', resolved.roomId)
        localStorage.setItem('roomHasPassword', 'false')
        window.history.replaceState({}, '', '/room')

        const requiresPassword = roomMetadataRequiresPassword(resolved.metadata)
        if (requiresPassword) {
          pendingInviteLinkJoinRef.current = {
            roomId: resolved.roomId,
            companyId: resolved.companyId ?? userCompanyId,
            customRoomId: resolved.customRoomId,
            joinVirtualRoomId: dlVr,
          }
          setSelectedRoomId(resolved.roomId)
          setShowPasswordDialog(true)
        } else {
          setPendingRoomJoin({
            roomId: resolved.roomId,
            password: null,
            isDefaultRoom: false,
            companyId: resolved.companyId ?? userCompanyId,
            customRoomId: resolved.customRoomId,
            origin: 'deep_link',
            joinVirtualRoomId: dlVr,
          })
          setShowPermissionsDialog(true)
        }
      } catch (error) {
        console.error('[MainMenu] Error processing booking deep link:', error)
        store.dispatch(addNotification({ message: 'Failed to process meeting link', type: 'error', duration: 5000 }))
      } finally {
        joinDeepLinkInFlightRef.current = false
      }
      return
    }

    const token = params.get('rid')
    if (!token) return

    joinDeepLinkInFlightRef.current = true
    try {
      joinStore.setEncryptedToken(token)
      const userCompanyId = store.getState().user.userData?.companyId ?? currentCompany?.id ?? null

      if (isServerInviteToken(token)) {
        const preview = await previewRoomInviteCloud(token)
        if (!preview.valid) {
          const msg =
            preview.errorCode === 'INVITE_EXPIRED'
              ? 'This room link has expired. Ask the host for a new link.'
              : preview.errorCode === 'INVITE_CONSUMED'
                ? 'This one-time link has already been used.'
                : 'This room link is invalid or was revoked.'
          store.dispatch(addNotification({ message: msg, type: 'error', duration: 6000 }))
          return
        }
        if (!preview.roomId && !preview.customRoomId) {
          store.dispatch(
            addNotification({ message: 'This room link is missing room information.', type: 'error', duration: 5000 })
          )
          return
        }

        const resolved = await resolveRoomForJoin(
          {
            roomId: preview.roomId ?? undefined,
            customRoomId: preview.customRoomId ?? undefined,
            companyId: preview.companyId ?? null,
            inviteToken: token,
          },
          userCompanyId
        )
        if (!resolved) {
          store.dispatch(
            addNotification({
              message:
                'Room not found. It may have been removed or the server restarted. Ask the host for a new link.',
              type: 'error',
              duration: 6000,
            })
          )
          return
        }

        const inviteExtras = {
          inviteToken: token,
          sharerUserIdForNear: preview.sharerUserId ?? null,
          defaultJoinNear: preview.defaultJoinNear !== false,
        }
        setInviteJoinNearHost(inviteExtras.defaultJoinNear)

        store.dispatch(
          addResolvedRoom({
            roomId: resolved.roomId,
            name: resolved.name,
            companyId: resolved.companyId,
            customRoomId: resolved.customRoomId,
            metadata: resolved.metadata,
          })
        )
        try {
          localStorage.removeItem('pendingRoomPassword')
        } catch {
          /* ignore */
        }
        const requiresPassword = roomMetadataRequiresPassword(resolved.metadata)
        localStorage.setItem('pendingRoomId', resolved.roomId)
        localStorage.setItem('roomHasPassword', requiresPassword ? 'true' : 'false')
        sessionStorage.setItem('app_join_from_invite', '1')
        window.history.replaceState({}, '', '/room')

        if (requiresPassword) {
          pendingInviteLinkJoinRef.current = {
            roomId: resolved.roomId,
            companyId: resolved.companyId ?? userCompanyId,
            customRoomId: resolved.customRoomId,
            ...inviteExtras,
          }
          setSelectedRoomId(resolved.roomId)
          setShowPasswordDialog(true)
        } else {
          setPendingRoomJoin({
            roomId: resolved.roomId,
            password: null,
            isDefaultRoom: false,
            companyId: resolved.companyId ?? userCompanyId,
            customRoomId: resolved.customRoomId,
            fromInviteLink: true,
            origin: 'invite_link',
            ...inviteExtras,
          })
          setShowPermissionsDialog(true)
        }
        return
      }

      const parsed = await useJoinStore.getState().decryptAndParseToken(token)
      if (!parsed) {
        console.error('[MainMenu] Failed to decrypt room token from link')
        store.dispatch(addNotification({ message: 'Invalid or expired room link', type: 'error', duration: 5000 }))
        return
      }
      const resolved = await resolveRoomForJoin(
        {
          roomId: parsed.roomId,
          customRoomId: parsed.customRoomId,
          companyId: parsed.companyId ?? null,
        },
        userCompanyId
      )
      if (resolved) {
        console.log('[MainMenu] Link-based join resolved, roomId:', resolved.roomId)
        store.dispatch(
          addResolvedRoom({
            roomId: resolved.roomId,
            name: resolved.name,
            companyId: resolved.companyId,
            customRoomId: resolved.customRoomId,
            metadata: resolved.metadata,
          })
        )
        try {
          localStorage.removeItem('pendingRoomPassword')
        } catch {
          /* ignore */
        }
        const requiresPassword = roomMetadataRequiresPassword(resolved.metadata)
        localStorage.setItem('pendingRoomId', resolved.roomId)
        localStorage.setItem('roomHasPassword', requiresPassword ? 'true' : 'false')
        sessionStorage.setItem('app_join_from_invite', '1')
        window.history.replaceState({}, '', '/room')

        if (requiresPassword) {
          pendingInviteLinkJoinRef.current = {
            roomId: resolved.roomId,
            companyId: resolved.companyId ?? userCompanyId,
            customRoomId: resolved.customRoomId,
          }
          setSelectedRoomId(resolved.roomId)
          setShowPasswordDialog(true)
          console.log('[MainMenu] Password dialog opened for link-based join (password-protected room)')
        } else {
          setPendingRoomJoin({
            roomId: resolved.roomId,
            password: null,
            isDefaultRoom: false,
            companyId: resolved.companyId ?? userCompanyId,
            customRoomId: resolved.customRoomId,
            fromInviteLink: true,
          })
          setShowPermissionsDialog(true)
          console.log('[MainMenu] Permissions dialog opened for link-based join')
        }
      } else {
        console.warn('[MainMenu] Could not resolve room from link - room may have been removed or server restarted')
        store.dispatch(
          addNotification({
            message: 'Room not found. It may have been removed or the server restarted. Ask the host for a new link.',
            type: 'error',
            duration: 6000,
          })
        )
      }
    } catch (error) {
      console.error('[MainMenu] Error processing link-based join:', error)
      store.dispatch(addNotification({ message: 'Failed to process room link', type: 'error', duration: 5000 }))
    } finally {
      joinDeepLinkInFlightRef.current = false
    }
  }, [roomJoined, showPermissionsDialog, showPasswordDialog, currentCompany?.id, joinStore, companyAccessGate.state])

  // A suspension that arrives while a join dialog is open closes it; the server
  // would refuse the join. These dialogs render outside the gated content, so they would
  // otherwise stay open over the suspended screen. The effect also re-runs on each dialog's
  // state: a permission check or room lookup still in flight when the suspension lands resumes
  // afterwards and reopens one, and the render gates alone would let reactivation pop it open.
  useEffect(() => {
    if (!companyGated) return
    if (showPermissionsDialog) setShowPermissionsDialog(false)
    if (showPasswordDialog) setShowPasswordDialog(false)
    if (pendingRoomJoin) setPendingRoomJoin(null)
  }, [
    companyGated,
    showPermissionsDialog,
    showPasswordDialog,
    pendingRoomJoin,
  ])

  useEffect(() => {
    void processJoinDeepLinkFromUrl()
  }, [processJoinDeepLinkFromUrl])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      ;(window as any).showDuplicateLoginConfirmation = (resolve: (value: boolean) => void) => {
        setDuplicateLoginConfirmPromise({ resolve })
      }
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).showDuplicateLoginConfirmation
      }
    }
  }, [])

  useEffect(() => {
    const unsub = useRoutingStore.subscribe(
      (s) => s.currentPath,
      () => {
        void processJoinDeepLinkFromUrl()
      }
    )
    const onPop = () => void processJoinDeepLinkFromUrl()
    window.addEventListener('popstate', onPop)
    return () => {
      unsub()
      window.removeEventListener('popstate', onPop)
    }
  }, [processJoinDeepLinkFromUrl])

  useEffect(() => {
    if (!showPermissionsDialog) {
      setInviteLinkGate({ loading: false, blocked: false })
      return
    }
    const tok = pendingRoomJoin?.inviteToken
    if (!tok || !isServerInviteToken(tok)) {
      setInviteLinkGate({ loading: false, blocked: false })
      return
    }
    let cancelled = false
    ;(async () => {
      setInviteLinkGate({ loading: true, blocked: false })
      try {
        const p = await previewRoomInviteCloud(tok)
        if (cancelled) return
        if (!p.valid) {
          const msg =
            p.errorCode === 'INVITE_EXPIRED'
              ? 'This room link has expired. Ask the host for a new link.'
              : p.errorCode === 'INVITE_CONSUMED'
                ? 'This one-time link has already been used.'
                : 'This room link is invalid or was revoked.'
          setInviteLinkGate({ loading: false, blocked: true, blockMessage: msg })
          return
        }
        if (p.allowedForCurrentUser === false) {
          setInviteLinkGate({
            loading: false,
            blocked: true,
            blockMessage:
              'This link is restricted to members of the host organization. Ask the host for an open link or switch accounts.',
          })
          return
        }
        setInviteLinkGate({ loading: false, blocked: false })
      } catch {
        if (!cancelled) {
          setInviteLinkGate({
            loading: false,
            blocked: true,
            blockMessage: 'Could not verify the room link. Check your connection and try again.',
          })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [showPermissionsDialog, pendingRoomJoin?.inviteToken, parseSessionReady, userData?.id])

  // Use the enhanced room refresh manager
  const {
    isRefreshing: roomRefreshLoading,
    refreshRooms: refreshRoomsFromManager,
    debouncedRefresh,
    forceRefresh,
    canRefresh,
    getRefreshStatus
  } = useRoomRefreshManager()

  // Legacy refreshRooms function for backward compatibility
  const refreshRooms = async (showSpinner = true) => {
    if (showSpinner) {
      return await forceRefresh()
    } else {
      return await refreshRoomsFromManager()
    }
  }



  // Remove serverLoad and memoryUsage from serverMetrics state
  const [serverMetrics, setServerMetrics] = useState({
    activeUsers: {
      value: 0,
      trend: 'neutral' as 'up' | 'down' | 'neutral',
      change: '0%',
    },
    totalRooms: {
      value: 0,
      trend: 'neutral' as 'up' | 'down' | 'neutral',
      change: '0%',
    },
  })

  // Update the useEffect to only set real metrics
  useEffect(() => {
    if (activeSection === 'adminMenu') {
      setServerMetrics({
        activeUsers: {
          value: getUserCount(),
          trend: 'up',
          change: '+5%',
        },
        totalRooms: {
          value: colyseusRooms.length,
          trend: 'neutral',
          change: '0%',
        },
      })
    }
  }, [activeSection, getUserCount, colyseusRooms.length])

  // Event handlers are no longer needed as stats are handled by the lobby service

  // Fetch map definitions when component mounts
  // Note: This loads directly from the static config file to avoid requiring Phaser
  useEffect(() => {
    const loadMapDefinitions = async () => {
      try {
        // Load map definitions from static config file (no Phaser needed)
        const response = await fetch('/assets/tilesets/config/tilesets.json')
        if (response.ok) {
          const tilesetConfig = await response.json()

          if (tilesetConfig && tilesetConfig.maps) {
            // Create a map of mapId to map name
            const mapsLookup: { [key: string]: { id: string; name: string; description: string } } =
              {}

            // Process each map definition
            tilesetConfig.maps.forEach((map: any) => {
              mapsLookup[map.id] = {
                id: map.id,
                name: map.name,
                description: map.description || '',
              }
            })

            // console.log('Loaded map definitions from tilesets_config:', mapsLookup)
            setMapDefinitions(mapsLookup)
          }
        }
      } catch (error) {
        // console.error('Error loading map definitions:', error)
      }
    }

    loadMapDefinitions()
  }, [])

  // Pagination helper functions
  const getPaginatedRooms = useCallback((rooms: any[], page: number) => {
    const startIndex = page * OPTIMIZATION_CONFIG.MAX_ROOMS_PER_PAGE
    return rooms.slice(startIndex, startIndex + OPTIMIZATION_CONFIG.MAX_ROOMS_PER_PAGE)
  }, [])

  const getTotalPages = useCallback((rooms: any[]) => {
    return Math.ceil(rooms.length / OPTIMIZATION_CONFIG.MAX_ROOMS_PER_PAGE)
  }, [])

  // Unified refresh function with 60-second cooldown
  const handleUnifiedRefresh = useCallback(async () => {
    if (roomsLoading || refreshCooldown || isLoadingLobbyStats || isRefreshing || roomJoined || companyDisabledState.isDisabled) return

    trackEvent('APP_mainmenu_refreshed', { section: activeSection })

    // Set cooldown for 60 seconds
    setRefreshCooldown(true)
    setTimeout(() => setRefreshCooldown(false), 60000)

    // Update last refresh time
    const now = Date.now()
    setLastRefreshTime(now)

    // Reset auto refresh timer: next auto refresh will be 60 seconds (cooldown) + 60 seconds (normal interval) from now
    const nextRefresh = now + 60000 + OPTIMIZATION_CONFIG.ROOM_REFRESH_INTERVAL
    setNextAutoRefreshTime(nextRefresh)
    // Update stable ref for countdown calculation
    stableNextRefreshTimeRef.current = nextRefresh

    // Emit event for manual refresh
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('roomsRefreshed', {
        detail: { timestamp: now, source: 'manual' }
      }))
    }

    try {
      // Refresh both user count and rooms simultaneously
      await Promise.all([
        refreshUserCount(),
        refreshRoomsFromManager()
      ])
    } catch (error) {
      console.error('Error during unified refresh:', error)
    }
  }, [roomsLoading, refreshCooldown, isLoadingLobbyStats, isRefreshing, roomJoined, refreshUserCount, refreshRoomsFromManager, companyDisabledState.isDisabled, activeSection])

  const getRefreshFeedback = useCallback(() => {
    // If user is in a room, show disabled state
    if (roomJoined) {
      return {
        message: 'In room - refresh disabled',
        color: 'default',
        disabled: true
      }
    }

    // If company is disabled, show disabled state
    if (companyDisabledState.isDisabled) {
      return {
        message: 'Company disabled - refresh disabled',
        color: 'warning',
        disabled: true
      }
    }

    const now = Date.now()
    const timeSinceLastRefresh = now - lastRefreshTime

    // Use the stable ref value, but update it if nextAutoRefreshTime has changed
    if (nextAutoRefreshTime > 0 && nextAutoRefreshTime !== stableNextRefreshTimeRef.current) {
      stableNextRefreshTimeRef.current = nextAutoRefreshTime
    }

    // Calculate time until next refresh using stable ref
    let timeUntilNextRefresh = stableNextRefreshTimeRef.current > 0
      ? Math.max(0, stableNextRefreshTimeRef.current - now)
      : 0

    if (refreshCooldown) {
      const cooldownRemaining = Math.max(0, Math.ceil((60000 - (now - lastRefreshTime)) / 1000))
      return {
        message: `Refresh cooldown: ${cooldownRemaining}s`,
        color: 'warning',
        disabled: true
      }
    }

    if (timeSinceLastRefresh < 10000) { // Less than 10 seconds
      const secondsAgo = Math.max(0, Math.floor(timeSinceLastRefresh / 1000))
      return {
        message: `Last refreshed: ${secondsAgo}s ago`,
        color: 'success',
        disabled: false
      }
    }

    if (timeUntilNextRefresh > 0) {
      // Calculate seconds remaining - use floor to show whole seconds
      const totalSeconds = Math.floor(timeUntilNextRefresh / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60

      // Show more detailed countdown for auto-refresh
      if (minutes > 0) {
        return {
          message: `Auto refresh in: ${minutes}m ${seconds}s`,
          color: 'info',
          disabled: false
        }
      } else {
        return {
          message: `Auto refresh in: ${seconds}s`,
          color: 'info',
          disabled: false
        }
      }
    }

    return {
      message: 'Ready to refresh',
      color: 'primary',
      disabled: false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRefreshTime, nextAutoRefreshTime, refreshCooldown, roomJoined, companyDisabledState.isDisabled, countdownTick])

  // Sync permission data to permission store
  // This ensures the permission store has the latest data from hooks
  const permissionStore = usePermissionStore.getState()

  useEffect(() => {
    // Sync account status data
    permissionStore.setAccountStatus({
      loading: accountStatusLoading,
      canCreateRooms,
      canJoinRooms,
      canJoinCustomRooms,
      canAccessDashboard,
      canAccessCompanyManagement,
      canAccessBilling,
      canAccessProfile,
      hasFullAccess,
      subscriptionStatus,
      paymentStatus,
      restrictions,
      statusMessage,
      needsAction,
      actionType,
      actionMessage,
    })
  }, [
    accountStatusLoading,
    canCreateRooms,
    canJoinRooms,
    canJoinCustomRooms,
    canAccessDashboard,
    canAccessCompanyManagement,
    canAccessBilling,
    canAccessProfile,
    hasFullAccess,
    subscriptionStatus,
    paymentStatus,
    restrictions,
    statusMessage,
    needsAction,
    actionType,
    actionMessage,
  ])

  // Sync company permissions data
  useEffect(() => {
    permissionStore.setCompanyPermissions({
      loading: companyPermissionsLoading,
      isCompanyManagerOrAdmin,
      hasCompanyAccess,
      currentCompany,
      isCompanyDisabled: companyDisabledState.isDisabled,
      canAccessCompanyManagement: companyCanAccessCompanyManagement,
    })
  }, [
    companyPermissionsLoading,
    isCompanyManagerOrAdmin,
    hasCompanyAccess,
    currentCompany,
    companyDisabledState.isDisabled,
    companyCanAccessCompanyManagement,
  ])

  // Memoize the hard block function to prevent infinite loops
  const companyOnlyBlock = useCallback((path: string) => path === '/company' || path.startsWith('/company/'), [])

  // Redirect managers/admins to Company Management when action is required
  useEffect(() => {
    // The suspended-company gate takes precedence (SC-409): the billing block would force
    // /company, which a suspended member cannot use, and would shut them out of /profile.
    if (companyGated) {
      routingStore.setHardBlock(undefined)
      return
    }

    const problematicSubscription = ['none', 'free_tier_exceeded', 'canceled', 'cancelled', 'suspended'].includes(subscriptionStatus)
    const problematicPayment = ['overdue', 'failed', 'suspended'].includes(paymentStatus)

    // Only hard block if user has no company access AND needs action
    // Company managers should be able to access dashboard/rooms even with billing issues
    const shouldHardBlock = needsAction && (problematicSubscription || problematicPayment) && !companyCanAccessCompanyManagement && !isCompanyManagerOrAdmin

    if (shouldHardBlock) {
      // Set hard block to only allow company routes
      routingStore.setHardBlock(companyOnlyBlock)

      // Only navigate if not already on a company route
      if (!routingStore.currentPath.startsWith('/company')) {
        routingStore.navigateTo('/company')
      }
    } else {
      // Clear hard block when no action needed or user has company management access
      routingStore.setHardBlock(undefined)
    }
  }, [companyGated, needsAction, companyCanAccessCompanyManagement, globalAdminLikeForBypass, isCompanyManagerOrAdmin, companyOnlyBlock, subscriptionStatus, paymentStatus]) // Include memoized function

  // CRITICAL: Company-specific default rooms - only show rooms for current user's company
  const companyDefaultRooms = useMemo(() => {
    return mappedDefaultRooms.filter((room) => {
      // Only show company rooms that belong to the current user's company
      const roomCompanyId = (room as any).metadata?.companyId
      return roomCompanyId && roomCompanyId === currentCompany?.id
    })
  }, [mappedDefaultRooms, currentCompany?.id])

  // Helper function to get the appropriate logo URL
  const getLogoUrl = (logoUrl: string | undefined) => {
    if (!logoUrl) return svgLogo
    // If the URL is a default path like /logo.png, use the imported SVG logo
    if (logoUrl === '/logo.png' || logoUrl === 'logo.png' || logoUrl === '/logo') {
      return svgLogo
    }
    return logoUrl
  }

  // Company branding logic - use company branding if available, otherwise fall back to platform branding
  // While gated, cloud returns no company object, so the workspace name would show instead.
  const displayName = companyGated
    ? companyAccessGate.companyName || 'Your company'
    : currentCompany?.platform_name || currentCompany?.name || platformName
  const displayLogo = getLogoUrl(currentCompany?.logo_url) || getLogoUrl(logoUrlState) || svgLogo

  // Ensure company disabled state is properly initialized
  useEffect(() => {
    if (companyDisabledState.isDisabled && process.env.NODE_ENV !== 'production') {
      console.log('[MainMenu] Company is disabled:', {
        reason: companyDisabledState.disabledReason,
        companyName: companyDisabledState.companyName,
        userRole: companyDisabledState.userRole,
        showAlert: companyDisabledState.showDisabledAlert
      })
    }
  }, [companyDisabledState.isDisabled, companyDisabledState.disabledReason, companyDisabledState.companyName, companyDisabledState.userRole, companyDisabledState.showDisabledAlert])

  // Get user's company role if available
  const [userCompanyRole, setUserCompanyRole] = useState<string | null>(null)
  // The role's current name when cloud sends it with the company answer (no extra request).
  const [userCompanyRoleName, setUserCompanyRoleName] = useState<string | null>(null)
  // True when cloud says the role shown is the fallback because none is stored for the user.
  const [userCompanyRoleIsDefault, setUserCompanyRoleIsDefault] = useState(false)

  // Helper function to get user's full name
  const getUserFullName = useCallback(
    () => getPersonDisplayName(userData),
    [userData?.displayName, userData?.firstName, userData?.lastName, userData?.username]
  )

  // Fetch user's company role when company data is available
  useEffect(() => {
    const fetchUserCompanyRole = async () => {
      if (currentCompany) {
        try {
          const companyResult = await CompanyService.getCurrentUserCompany()
          // A user with no stored role has no `user_role`, but cloud still names their role, so
          // the profile shows it (never blank) rather than keeping the previous company's.
          if (companyResult.success && (companyResult.user_role || companyResult.role_name)) {
            setUserCompanyRole(companyResult.user_role || null)
            setUserCompanyRoleName(companyResult.role_name ?? null)
            setUserCompanyRoleIsDefault(companyResult.role_is_default === true)
          }
        } catch (error) {
          console.error('Error fetching user company role:', error)
        }
      } else {
        setUserCompanyRole(null)
        setUserCompanyRoleName(null)
        setUserCompanyRoleIsDefault(false)
      }
    }

    fetchUserCompanyRole()
  }, [currentCompany])

  /**
   * Every company's ColyseusRoom rows, marked running from the server's running-room list. A room
   * that isn't running still shows; joining it starts it.
   */
  const loadAdminRooms = async (): Promise<OptimizedRoom[]> => {
    const [rows, running] = await Promise.all([
      ColyseusRoomService.listAll(),
      fetch(`${getServerBaseUrl()}/api/room-control/rooms`, {
        headers: { Authorization: `Bearer ${getStoredSessionToken()}` },
      })
        .then((res) => (res.ok ? res.json() : { rooms: [] }))
        .then((body) => body.rooms || [])
        .catch(() => []),
    ])
    return adminCompanyRooms(running, rows)
  }

  // Fetch all companies for admin view
  useEffect(() => {
    const fetchAllCompanies = async () => {
      // Fetch companies for any admin section, not just adminAllRooms
      if (serverVerifiedElevated && (activeSection === 'adminAllRooms' || activeSection === 'adminMenu')) {
        setLoadingAllCompanies(true)
        try {
          if (activeSection === 'adminAllRooms') {
            // Fetch companies, usage and every company's rooms in parallel to avoid waterfall
            const [companiesResult, usage, rooms] = await Promise.all([
              CompanyService.getAllCompanies(),
              lobbyService.getCompanyUsage(true),
              loadAdminRooms(),
            ])
            if (companiesResult.success && companiesResult.companies) {
              setAllCompanies(companiesResult.companies)
            }
            setCompanyUsage(usage)
            setAdminRooms(rooms)
          } else {
            const result = await CompanyService.getAllCompanies()
            if (result.success && result.companies) {
              setAllCompanies(result.companies)
            }
          }
        } catch (error) {
          console.error('Error fetching all companies:', error)
        } finally {
          setLoadingAllCompanies(false)
        }
      }
    }

    fetchAllCompanies()
  }, [activeSection, serverVerifiedElevated])

  // Debug logging for company permissions (only in development and when there's an issue)
  if (process.env.NODE_ENV !== 'production' && (companyPermissionsError || companyDisabledState.isDisabled)) {
    console.log('[MainMenu] Company permissions state:', {
      hasCompanyAccess,
      isCompanyManagerOrAdmin,
      isGlobalAdmin,
      currentCompany: currentCompany?.name,
      userCompanyRole,
      loading: companyPermissionsLoading,
      error: companyPermissionsError,
      globalAdminLikeForBypass
    })
  }

  // Debug logging for company disabled state (only in development and when disabled)
  if (process.env.NODE_ENV !== 'production' && companyDisabledState.isDisabled) {
    console.log('[MainMenu] Company disabled state:', {
      isDisabled: companyDisabledState.isDisabled,
      disabledReason: companyDisabledState.disabledReason,
      companyName: companyDisabledState.companyName,
      userRole: companyDisabledState.userRole,
      isAdmin: companyDisabledState.isAdmin
    })
  }

  // Debug function for testing global admin access and account status
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      (window as any).debugCompanyPermissions = {
        forceAdmin: () => {
          localStorage.setItem('forceAdmin', 'true')
          window.location.reload()
        },
        removeForceAdmin: () => {
          localStorage.removeItem('forceAdmin')
          window.location.reload()
        },
        checkPermissions: () => {
          console.log('Current permissions:', {
            hasCompanyAccess,
            isCompanyManagerOrAdmin,
            isGlobalAdmin,
            globalAdminLikeForBypass,
            userCompanyRole,
            userFullName: getUserFullName(),
            forceAdmin: localStorage.getItem('forceAdmin')
          })
        },
        checkAccountStatus: () => {
          console.log('Current account status:', {
            accountStatus,
            canCreateRooms,
            canJoinRooms,
            canJoinCustomRooms,
            canAccessDashboard,
            canAccessCompanyManagement,
            canAccessBilling,
            subscriptionStatus,
            paymentStatus,
            restrictions,
            statusMessage,
            needsAction,
            actionType,
            actionMessage
          })
        },
        refreshAccountStatus: () => {
          refreshAccountStatus()
        },
        simulateDisabledCompany: () => {
          // Simulate a disabled company for testing
          console.log('Simulating disabled company state...')
          // Force the disabled state for testing
          localStorage.setItem('simulateDisabledCompany', 'true')
          window.location.reload()
        },
        removeSimulatedDisabledCompany: () => {
          localStorage.removeItem('simulateDisabledCompany')
          window.location.reload()
        }
      }
    }
  }, [hasCompanyAccess, isCompanyManagerOrAdmin, isGlobalAdmin, globalAdminLikeForBypass, accountStatus, canCreateRooms, canJoinRooms, canJoinCustomRooms, canAccessDashboard, canAccessCompanyManagement, canAccessBilling, subscriptionStatus, paymentStatus, restrictions, statusMessage, needsAction, actionType, actionMessage, refreshAccountStatus])

  // Render content based on active section
  const renderContent = () => {
    // SC-409: default-deny. While the company is suspended or being deleted, every section
    // that is not on SECTIONS_AVAILABLE_WHEN_COMPANY_SUSPENDED shows the disabled screen,
    // whether reached from the sidebar, a typed URL, a deep link or a section added later.
    if (companyGated && !isAvailableWhenCompanySuspended(activeSection)) {
      return (
        <CompanyDisabledScreen
          companyName={companyAccessGate.companyName}
          reason={companyAccessGate.reason}
        />
      )
    }

    // Legacy detection from the cloud error text, kept for when the access check has failed
    // (the gate fails open). Same screen, without a company name: cloud returns none.
    if (!companyGated && companyDisabledState.isDisabled && isCompanyGatedSection(activeSection)) {
      return <CompanyDisabledScreen companyName={companyDisabledState.companyName} reason="suspended" />
    }

    switch (activeSection) {
      case 'publicLobby':
        return (
          <>
            <ContentHeader>
              <PageTitle>{dashboardGreeting(userData)}</PageTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  icon={<PeopleIcon />}
                  label={
                    isLoadingLobbyStats || isRefreshing ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} sx={{ color: 'inherit' }} />
                        <span>Updating users...</span>
                      </Box>
                    ) : (
                      `${getUserCount()} ${getUserCount() === 1 ? 'user' : 'users'} online`
                    )
                  }
                  sx={{
                    backgroundColor: isLoadingLobbyStats || isRefreshing
                      ? alpha(theme.palette.warning.main, 0.15)
                      : alpha(displayPrimaryColor, 0.15),
                    color: isLoadingLobbyStats || isRefreshing ? theme.palette.warning.main : displayPrimaryColor,
                    fontWeight: 600,
                    borderRadius: '8px',
                    padding: '4px 8px',
                    minWidth: '160px',
                    transition: 'all 0.3s ease',
                    '& .MuiChip-icon': {
                      color: isLoadingLobbyStats || isRefreshing ? theme.palette.warning.main : displayPrimaryColor,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={getRefreshFeedback().message}
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      height: '24px',
                      backgroundColor: getRefreshFeedback().color === 'warning'
                        ? alpha(theme.palette.warning.main, 0.2)
                        : getRefreshFeedback().color === 'success'
                          ? alpha(theme.palette.success.main, 0.2)
                          : getRefreshFeedback().color === 'info'
                            ? alpha(theme.palette.info.main, 0.2)
                            : alpha(displayPrimaryColor, 0.2),
                      color: getRefreshFeedback().color === 'warning'
                        ? theme.palette.warning.main
                        : getRefreshFeedback().color === 'success'
                          ? theme.palette.success.main
                          : getRefreshFeedback().color === 'info'
                            ? theme.palette.info.main
                            : displayPrimaryColor,
                      border: '1px solid',
                      borderColor: getRefreshFeedback().color === 'warning'
                        ? alpha(theme.palette.warning.main, 0.3)
                        : getRefreshFeedback().color === 'success'
                          ? alpha(theme.palette.success.main, 0.3)
                          : getRefreshFeedback().color === 'info'
                            ? alpha(theme.palette.info.main, 0.3)
                            : alpha(displayPrimaryColor, 0.3),
                    }}
                  />
                  <Tooltip title={getRefreshFeedback().message}>
                    {(getRefreshFeedback().disabled || roomJoined) ? (
                      <span>
                        <IconButton
                          onClick={handleUnifiedRefresh}
                          disabled={true}
                          sx={{
                            mr: 1,
                            color: 'text.disabled',
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                          size="small"
                        >
                          {roomsLoading || isLoadingLobbyStats || isRefreshing ? (
                            <CircularProgress size={18} sx={{ color: displayPrimaryColor }} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    ) : (
                      <IconButton
                        onClick={handleUnifiedRefresh}
                        disabled={false}
                        sx={{
                          mr: 1,
                          color: displayPrimaryColor,
                          '&:hover': {
                            backgroundColor: alpha(displayPrimaryColor, 0.1),
                          },
                        }}
                        size="small"
                      >
                        {roomsLoading || isLoadingLobbyStats || isRefreshing ? (
                          <CircularProgress size={18} sx={{ color: displayPrimaryColor }} />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Tooltip>
                </Box>
              </Box>
            </ContentHeader>

            {/* Rooms on the left half, upcoming meetings on the right. */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <DashboardRoomList
                  rooms={companyRooms}
                  loading={roomsLoading}
                  onJoin={(room) => handleJoinRoom(room.id, room.metadata?.companyId ?? undefined)}
                  disabled={!lobbyJoined || accountStatusLoading}
                  refresh={{
                    onRefresh: handleUnifiedRefresh,
                    tooltip: getRefreshFeedback().message,
                    disabled: getRefreshFeedback().disabled || roomJoined || roomsLoading || isLoadingLobbyStats || isRefreshing,
                    busy: roomsLoading || isLoadingLobbyStats || isRefreshing,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <UpcomingMeetingsList roomNames={companyRoomNames} onViewAll={() => handleSectionChange('meetings')} />
              </Grid>
            </Grid>
          </>
        )

      case 'myRooms':
        return (
          <>
            <ContentHeader>
              <PageTitle>Rooms</PageTitle>
              {/* SC-422: only people holding rooms.manage (Owner and Admin by default, and platform admins) create and manage rooms. */}
              <ManageRoomsButton
                companyId={currentCompany?.id}
                onClick={() => setShowManageRoomsDialog(true)}
              />
            </ContentHeader>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Tooltip title={getRefreshFeedback().message}>
                  {getRefreshFeedback().disabled || roomJoined ? (
                    <span>
                      <IconButton
                        onClick={handleUnifiedRefresh}
                        disabled={true}
                        sx={{
                          mr: 1,
                          color: 'text.disabled',
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        }}
                        size="small"
                      >
                        {roomsLoading || isLoadingLobbyStats || isRefreshing ? (
                          <CircularProgress size={18} sx={{ color: displayPrimaryColor }} />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  ) : (
                    (roomsLoading || isLoadingLobbyStats || isRefreshing) ? (
                      <span>
                        <IconButton
                          onClick={handleUnifiedRefresh}
                          disabled={true}
                          sx={{
                            mr: 1,
                            color: displayPrimaryColor,
                            backgroundColor: alpha(displayPrimaryColor, 0.1),
                            border: `1px solid ${alpha(displayPrimaryColor, 0.2)}`,
                            '&:hover': {
                              backgroundColor: alpha(displayPrimaryColor, 0.2),
                              borderColor: alpha(displayPrimaryColor, 0.3),
                              transform: 'scale(1.05)',
                            },
                          }}
                          size="small"
                        >
                          <CircularProgress size={18} sx={{ color: displayPrimaryColor }} />
                        </IconButton>
                      </span>
                    ) : (
                      <IconButton
                        onClick={handleUnifiedRefresh}
                        disabled={false}
                        sx={{
                          mr: 1,
                          color: displayPrimaryColor,
                          backgroundColor: alpha(displayPrimaryColor, 0.1),
                          border: `1px solid ${alpha(displayPrimaryColor, 0.2)}`,
                          '&:hover': {
                            backgroundColor: alpha(displayPrimaryColor, 0.2),
                            borderColor: alpha(displayPrimaryColor, 0.3),
                            transform: 'scale(1.05)',
                          },
                        }}
                        size="small"
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    )
                  )}
                </Tooltip>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  All rooms
                </Typography>
              </Box>

              {roomsLoading ? (
                <CircularProgress size={32} sx={{ color: displayPrimaryColor, mb: 2 }} />
              ) : companyRooms.length > 0 ? (
                // Same grid as the Dashboard's company rooms.
                <Grid container spacing={2} alignItems="stretch">
                  {companyRooms.map((room) => (
                    <Grid item xs={12} sm={6} md={4} key={room.id}>
                      <RoomCard
                        room={room}
                        onJoin={() => handleJoinRoom(room.id, (room as any).metadata?.companyId)}
                        disabled={!lobbyJoined || accountStatusLoading}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    boxShadow: 1,
                    backgroundImage: 'none',
                  }}
                >
                  <MeetingRoomIcon
                    sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                  />
                  <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
                    No rooms yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                    {canManageRooms ? 'Add one with Manage rooms.' : 'Your company admins can add rooms.'}
                  </Typography>
                </Box>
              )}
            </Box>

            {currentCompany?.id && (
              <CompanyRoomsDialog
                open={showManageRoomsDialog}
                onClose={() => {
                  setShowManageRoomsDialog(false)
                  // Rooms added, renamed or reordered in the dialog show here straight away.
                  refreshRoomsFromHook()
                }}
                company={{ id: currentCompany.id, name: currentCompany.name }}
                isPlatformAdmin={serverVerifiedElevated}
              />
            )}
          </>
        )

      case 'meetings':
        return <MeetingsDashboardPanel />

      case 'chat':
        return <ChatPage unavailable={isGuestUserId(userData?.id)} />

      case 'profile':
        return (
          <ProfileMainMenuPanel
            userData={userData}
            companyRole={userCompanyRole}
            companyRoleName={userCompanyRoleName}
            companyRoleIsDefault={userCompanyRoleIsDefault}
            isPlatformAdmin={serverVerifiedElevated}
            onEditProfile={() => {
              routingStore.navigateTo('/profile/edit')
              setShowProfileDialog(true)
            }}
          />
        )

      case 'adminMenu':
        return (
          <>
            <ContentHeader>
              <PageTitle>Admin Dashboard</PageTitle>
            </ContentHeader>

            {/* User-Friendly Platform Overview Dashboard */}
            <Box sx={adminOverviewPaperSx(theme)}>
              {/* Clear Header */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                    marginBottom: '4px'
                  }}
                >
                  Platform Overview
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                  }}
                >
                  Current system status and key metrics
                </Typography>
              </Box>

              {/* Status Bar */}
              <Box sx={adminOverviewStatusBarSx(theme)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: theme.palette.success.main,
                    animation: `${pulse} 2s infinite`
                  }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      fontWeight: 500
                    }}
                  >
                    All systems operational
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                  }}
                >
                  Last updated: {new Date().toLocaleTimeString()}
                </Typography>
              </Box>

              {/* Metrics Cards - Simplified Design */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={adminMetricTileSx(theme, displayPrimaryColor)}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        marginBottom: '4px',
                        fontSize: '2rem'
                      }}
                    >
                      {serverMetrics.activeUsers.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    >
                      Active Users
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={adminMetricTileSx(theme, displayPrimaryColor)}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        marginBottom: '4px',
                        fontSize: '2rem'
                      }}
                    >
                      {serverMetrics.totalRooms.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    >
                      Total Rooms
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={adminMetricTileSx(theme, displayPrimaryColor)}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        marginBottom: '4px',
                        fontSize: '2rem'
                      }}
                    >
                      {colyseusRooms.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    >
                      Online Rooms
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={adminMetricTileSx(theme, displayPrimaryColor)}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        marginBottom: '4px',
                        fontSize: '2rem'
                      }}
                    >
                      {loadingAllCompanies ? '...' : allCompanies.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    >
                      Companies
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

            </Box>

            <SectionDivider>
              <SectionTitle>Admin Tools</SectionTitle>
            </SectionDivider>

            <AdminMenuContainer>
              <AdminMenuCard
                onClick={() => handleSectionChange('adminEnvironmentConfig')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <SettingsApplicationsIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Environment Configuration
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    Configure platform settings and registration restrictions
                  </Typography>
                </CardContent>
              </AdminMenuCard>

              <AdminMenuCard
                onClick={() => handleSectionChange('adminEntityCatalogue')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <CategoryIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Entity Catalogue
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    Create and manage entities and categories for the map editor
                  </Typography>
                </CardContent>
              </AdminMenuCard>


              <AdminMenuCard
                onClick={() => handleSectionChange('adminBillingSettings')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <SettingsApplicationsIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Billing Settings
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    Configure pricing tiers and initialize default plans
                  </Typography>
                </CardContent>
              </AdminMenuCard>

              <AdminMenuCard
                onClick={() => handleSectionChange('adminServerJobs')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <ScheduleIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Server Jobs
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    Monitor and manage automated server tasks and cron jobs
                  </Typography>
                </CardContent>
              </AdminMenuCard>

              <AdminMenuCard
                onClick={() => handleSectionChange('adminMaps')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
                data-testid="admin-maps-card"
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <MapIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Maps
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    Register maps, set their start point, reception and lobby use, and edit their virtual rooms
                  </Typography>
                </CardContent>
              </AdminMenuCard>

              <AdminMenuCard
                onClick={() => handleSectionChange('adminTransactionalEmails')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <EmailOutlinedIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Transactional emails
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    Audit log of sent templated mail by company, user, category, and template
                  </Typography>
                </CardContent>
              </AdminMenuCard>

              <AdminMenuCard
                onClick={() => handleSectionChange('adminAllRooms')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <MeetingRoomIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    All Rooms (Admin View)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    View all rooms across all companies with company-specific organization
                  </Typography>
                </CardContent>
              </AdminMenuCard>

              <AdminMenuCard
                onClick={() => handleSectionChange('adminUserManagement')}
                sx={adminToolCardFullSx(theme, displayPrimaryColor)}
              >
                <CardIconWrapper
                  sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                >
                  <PersonIcon sx={{ fontSize: '28px' }} />
                </CardIconWrapper>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '12px',
                      fontSize: '1.1rem',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    User Management
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      fontWeight: 400
                    }}
                  >
                    List, select, and completely delete users from the platform
                  </Typography>
                </CardContent>
              </AdminMenuCard>

              {/* Product updates editor — global admins only */}
              {(serverVerifiedElevated) && (
                <AdminMenuCard
                  onClick={() => handleSectionChange('adminProductUpdates')}
                  sx={adminToolCardFullSx(theme, displayPrimaryColor)}
                >
                  <CardIconWrapper
                    sx={adminToolCardIconWrapperSx(theme, displayPrimaryColor)}
                  >
                    <NewReleasesIcon sx={{ fontSize: '28px' }} />
                  </CardIconWrapper>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        marginBottom: '12px',
                        fontSize: '1.1rem',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {"Changelog & roadmap"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                        fontWeight: 400,
                      }}
                    >
                      Edit in-app release notes and roadmap (stored in the database)
                    </Typography>
                  </CardContent>
                </AdminMenuCard>
              )}

              {/* Endpoint Testing Card - Only for Global Admins */}
              {(serverVerifiedElevated) && (
                <AdminMenuCard
                  onClick={() => handleSectionChange('adminEndpointTesting')}
                  sx={adminToolDangerCardFullSx(theme)}
                >
                  <CardIconWrapper
                    sx={adminToolDangerIconWrapperSx(theme)}
                  >
                    <BugReportIcon sx={{ fontSize: '28px' }} />
                  </CardIconWrapper>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        marginBottom: '12px',
                        fontSize: '1.1rem',
                        letterSpacing: '-0.01em'
                      }}
                    >
                      Endpoint Testing
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                        fontWeight: 400
                      }}
                    >
                      Test server endpoints, routes, and API functionality
                    </Typography>
                  </CardContent>
                </AdminMenuCard>
              )}
            </AdminMenuContainer>
          </>
        )

      case 'adminEnvironmentConfig':
        return (
          <AdminEnvironmentConfig onSectionChange={handleSectionChange} />
        )

      case 'adminEntityCatalogue':
        return (
          <>
            <ContentHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={() => handleSectionChange('adminMenu')}
                  sx={{
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(-2px)',
                                backgroundColor: alpha(displayPrimaryColor, 0.1),
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <PageTitle>Entity Catalogue</PageTitle>
              </Box>
            </ContentHeader>
            <Box
              sx={{
                animation: 'fadeIn 0.4s ease',
                '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
              }}
            >
              <EntityCatalogueAdminPanel />
            </Box>
          </>
        )


      case 'adminBillingSettings':
        return (
          <AdminBillingSettings onSectionChange={handleSectionChange} />
        )

      case 'adminServerJobs':
        return (
          <ServerJobsPanel onClose={() => handleSectionChange('adminMenu')} />
        )

      case 'adminMaps':
        return <AdminMaps onBack={() => handleSectionChange('adminMenu')} />

      case 'adminTransactionalEmails':
        return (
          <TransactionalEmailAuditPanel onBack={() => handleSectionChange('adminMenu')} />
        )

      case 'adminUserManagement':
        return (
          <UserManagementCard />
        )

      case 'adminCompanyManagement':
        return (
          <CompanyManagementPanel onBack={() => handleSectionChange('adminMenu')} />
        )

      case 'adminEndpointTesting':
        return (
          <EndpointTestingPanel onBack={() => handleSectionChange('adminMenu')} />
        )

      case 'adminProductUpdates':
        return (
          <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
            <AdminProductUpdatesPanel onBack={() => handleSectionChange('adminMenu')} />
          </Box>
        )

      case 'adminAllRooms':
        return (
          <>
            <ContentHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={() => handleSectionChange('adminMenu')}
                  sx={{
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(-2px)',
                                backgroundColor: alpha(displayPrimaryColor, 0.1),
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <PageTitle>All Rooms (Admin View)</PageTitle>
              </Box>
            </ContentHeader>

            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              View all rooms across all companies. This view is only available to platform administrators.
            </Typography>

            {/* Per-company online users and room counts (inlined below in accordions via companyUsage) */}

            {loadingAllCompanies ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: displayPrimaryColor }} />
                <Typography variant="body1" sx={{ color: 'text.secondary', ml: 2 }}>
                  Loading company data...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(roomsByCompany).map(([companyId, { company, rooms }]) => (
                  <Accordion
                    key={companyId}
                    expanded={adminAllRoomsExpanded[companyId] || false}
                    onChange={() => setAdminAllRoomsExpanded(prev => ({
                      ...prev,
                      [companyId]: !prev[companyId]
                    }))}
                    sx={{
                      backgroundColor: theme.palette.action.hover,
                      color: 'text.primary',
                      '&:before': { display: 'none' },
                      '& .MuiAccordionSummary-root': {
                        backgroundColor: companyId === 'global'
                          ? alpha(displayPrimaryColor, 0.1)
                          : alpha(theme.palette.success.main, 0.1),
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: companyId === 'global'
                            ? alpha(displayPrimaryColor, 0.2)
                            : alpha(theme.palette.success.main, 0.2),
                        },
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      },
                      '& .MuiAccordionDetails-root': {
                        padding: '16px',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        adminAllRoomsExpanded[companyId]
                          ? <ExpandLessIcon sx={{ color: 'text.primary' }} />
                          : <ExpandMoreIcon sx={{ color: 'text.primary' }} />
                      }
                      sx={{ borderRadius: '8px' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {companyId === 'global' ? (
                            <MeetingRoomIcon sx={{ color: displayPrimaryColor }} />
                          ) : (
                            <BusinessIcon sx={{ color: 'success.main' }} />
                          )}
                          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {company.name} - Rooms
                          </Typography>
                          <Chip
                            label={(() => {
                              const onlineCount = companyUsage[companyId || 'global']?.totalUsers ?? 0
                              const rosterCompany = companyId !== 'global'
                                ? allCompanies.find(c => c.id === companyId)
                                : null
                              const rosterCount = rosterCompany?.total_users
                              return rosterCount != null
                                ? `${rooms.length} rooms · ${rosterCount} members · ${onlineCount} online`
                                : `${rooms.length} rooms · ${onlineCount} online`
                            })()}
                            size="small"
                            sx={{
                              backgroundColor: companyId === 'global'
                                ? alpha(displayPrimaryColor, 0.2)
                                : alpha(theme.palette.success.main, 0.2),
                              color: companyId === 'global' ? displayPrimaryColor : 'success.main',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </Box>
                        {/* Join Lobby button for non-global companies */}
                        {companyId !== 'global' && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<HomeIcon />}
                            onClick={(e) => {
                              e.stopPropagation() // Prevent accordion from expanding/collapsing
                              joinAnyCompanyLobby(companyId, company.name)
                            }}
                            sx={{
                              backgroundColor: alpha(displayPrimaryColor, 0.9),
                              color: theme.palette.getContrastText(displayPrimaryColor),
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              px: 2,
                              py: 0.5,
                              minWidth: 'auto',
                              '&:hover': {
                                backgroundColor: displayPrimaryColor,
                              },
                            }}
                          >
                            Join Lobby
                          </Button>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {rooms.length > 0 ? (
                        <Grid container spacing={2} alignItems="stretch">
                          {rooms.map((room) => (
                            <Grid item xs={12} sm={6} md={4} key={room.id}>
                              <RoomCard
                                room={room}
                                onJoin={() => {
                                  console.log(`[Admin View] Joining room:`, {
                                    id: room.id,
                                    name: room.name,
                                    customRoomId: room.customRoomId,
                                    companyId: room.metadata?.companyId
                                  })
                                  handleJoinRoom(room.id, room.metadata?.companyId)
                                }}
                                disabled={!lobbyJoined || accountStatusLoading}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 3,
                            backgroundColor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px',
                            boxShadow: 1,
                            backgroundImage: 'none',
                          }}
                        >
                          <MeetingRoomIcon sx={{ fontSize: 32, mb: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            No rooms available for {company.name}
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}

                {Object.keys(roomsByCompany).length === 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 4,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                      boxShadow: 1,
                      backgroundImage: 'none',
                    }}
                  >
                    <MeetingRoomIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                    />
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
                      No rooms found across all companies
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      Rooms will appear here once companies start creating them.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )

      case 'companyManagement':
        if (companyPermissionsLoading) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress sx={{ color: 'primary.main' }} />
            </Box>
          )
        }

        if (companyPermissionsError && !globalAdminLikeForBypass) {
          return (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => handleSectionChange('publicLobby')} sx={{ mr: 2 }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Company Management
                </Typography>
              </Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                Error loading company information: {companyPermissionsError}
              </Alert>
            </Box>
          )
        }

        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('publicLobby')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
            initialCompanyTab="overview"
          />
        )

      // Company Management Sub-sections
      case 'companyOverview':
        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('companyManagement')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
            initialCompanyTab={'overview'}
          />
        )

      case 'companyDashboard':
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => handleSectionChange('companyManagement')} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Company Dashboard
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Company dashboard content will be implemented here.
            </Typography>
          </Box>
        )

      case 'companySetup':
        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('publicLobby')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
            initialCompanyTab="setup"
          />
        )

      case 'companyBilling':
        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('companyManagement')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
            initialCompanyTab={'billing'}
          />
        )

      case 'companyUsers':
        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('companyManagement')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
            initialCompanyTab={'users'}
          />
        )

      case 'companySettings':
        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('companyManagement')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
            initialCompanyTab={'settings'}
          />
        )

      case 'companyWallet':
        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('companyManagement')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
            initialCompanyTab={'wallet' as const}
          />
        )

      // Admin Sub-sections
      case 'adminDashboard':
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => handleSectionChange('adminMenu')} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Admin Dashboard
              </Typography>
            </Box>
            <AdminMetrics metrics={serverMetrics} />
          </Box>
        )

      case 'adminRoomManagement':
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => handleSectionChange('adminMenu')} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Room Management
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Room management content will be implemented here.
            </Typography>
          </Box>
        )

      case 'adminMapEditorPlugins':
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => handleSectionChange('adminMenu')} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Map Editor Plugins
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Map editor plugins content will be implemented here.
            </Typography>
          </Box>
        )

      // Routing Test (temporary for development)
      case 'routingTest':
        return <RoutingTest />

      // Company Management Dialogs (these will be handled by dialog state management)
      case 'companyDialog':
      case 'inviteUserDialog':
      case 'editUserDialog':
      case 'usersDialog':
      case 'companyRoomsDialog':
        // These dialogs should be handled by the CompanyManagement components
        // Redirect to company management and let the components handle dialog state
        return (
          <CompanyManagementPanel
            onBack={() => handleSectionChange('publicLobby')}
            isPlatformAdmin={globalAdminLikeForBypass}
            isCompanyManagerOrAdmin={isCompanyManagerOrAdmin}
            isGlobalAdmin={globalAdminLikeForBypass}
            currentCompany={currentCompany}
          />
        )

      default:
        return null
    }
  }

  // Check email verification status and redirect if not verified
  useEffect(() => {
    const checkEmailVerification = async () => {
      // Skip check if already on verification page
      if (normalizeRoutePath(window.location.pathname) === '/verify-email') {
        return
      }

      // Skip check if user is joining through a link
      const params = new URLSearchParams(window.location.search)
      const hasJoinLink = params.get('rid') || params.get('room')
      if (hasJoinLink) {
        return
      }

      // Check if user is authenticated
      if (!userData?.id) {
        return
      }

      // Check email verification status
      // If emailVerified is explicitly false, redirect to verification page
      if (userData.emailVerified === false) {
        console.log('[MainMenu] Email not verified, redirecting to verification page')
        window.location.href = '/verify-email'
        return
      }

      // Also check from Parse user object as fallback
      try {
        const currentUser = Parse.User.current()
        if (currentUser) {
          const parseEmailVerified = currentUser.get('emailVerified')
          if (parseEmailVerified === false) {
            console.log('[MainMenu] Email not verified (from Parse), redirecting to verification page')
            window.location.href = '/verify-email'
            return
          }
        }
      } catch (error) {
        console.error('[MainMenu] Error checking Parse user email verification:', error)
      }
    }

    checkEmailVerification()
  }, [userData?.id, userData?.emailVerified])

  useEffect(() => {
    // After auth/session is ready, ensure sane landing route
    // Skip redirect if user is joining through a link (has ?rid= or ?room= parameter) OR if permissions dialog is open
    const params = new URLSearchParams(window.location.search)
    const hasJoinLink = params.get('rid') || params.get('room')

    // Don't redirect if:
    // 1. User is joining through a link (has ?rid= parameter) - they should go directly to meeting rooms
    // 2. Permissions dialog is currently open (link-based join in progress)
    // 3. Already on a valid route (not just '/')
    // 4. User is already in a room
    // 5. User is on verification page
    const pathNow = normalizeRoutePath(window.location.pathname)
    if (
      hasJoinLink ||
      showPermissionsDialog ||
      roomJoined ||
      pathNow === '/verify-email' ||
      pathNow === '/accept-invitation' ||
      pathNow === '/invitation-email-preview'
    ) {
      // User is joining through a link - let the deep link handler and permissions dialog take care of it
      // Don't redirect to dashboard/company - wait for permissions dialog to complete or proceed to room
      return
    }

    if (!parseSessionReady || !useRouting || companyPermissionsLoading) return
    // A suspended member gets no company object from cloud, so deciding before the access
    // check answers would send them to the create-workspace form (SC-409).
    if (companyAccessGate.state === 'pending') return
    if (isWorkspaceLandingHandled()) return

    const currentUserHasCompany = !!currentCompany?.id || !!companyAccessGate.companyId
    const pathForLanding = normalizeRoutePath(
      routingStore.currentPath || window.location.pathname,
    )
    // The workspace settings are already in the store here: AppController renders nothing (so
    // no MainMenu) until its settings load has finished. After its 8 s race, getSettings starts
    // loadSettings again if the first load is still out, and loadSettings never throws. Settings
    // that still come back null (a timed-out fetch) count as Open by design; cloud still refuses
    // self-serve company creation while Closed. [SC-443]
    const desired = getOneTimeWorkspaceLandingRedirect(pathForLanding, currentUserHasCompany, {
      companyBlocked: companyGated,
      registrationClosed: isRegistrationClosed,
    })
    markWorkspaceLandingHandled()
    if (desired) {
      console.log(`[MainMenu] One-time workspace landing redirect to: ${desired} (using replace)`)
      routingStore.navigateTo(desired, true)
    }
  }, [
    parseSessionReady,
    currentCompany?.id,
    companyPermissionsLoading,
    useRouting,
    showPermissionsDialog,
    roomJoined,
    routingStore.currentPath,
    companyAccessGate.state,
    companyAccessGate.companyId,
    companyGated,
    isRegistrationClosed,
  ])

  const navTooltipPlacement = isMobile ? 'bottom' : 'right'

  const renderSidebarNav = (): React.ReactNode => (
    <>
    <LogoContainer>
      <Box className="logo-badge">
        <Box
          sx={{
            width: 32,
            height: 32,
            backgroundColor: displayPrimaryColor,
            mask: `url(${displayLogo}) no-repeat center`,
            maskSize: 'contain',
            WebkitMask: `url(${displayLogo}) no-repeat center`,
            WebkitMaskSize: 'contain'
          }}
        />
      </Box>
      <Typography className="brand-name" variant="subtitle1">
        {displayName}
      </Typography>
      <Typography className="brand-subtext" variant="caption">
        Company Management
      </Typography>
    </LogoContainer>

    <MenuList>
      {/* Suspended companies: a new tab is closed unless added to
          SECTIONS_AVAILABLE_WHEN_COMPANY_SUSPENDED (MainMenu/utils/suspendedCompanyGate.ts).
          Decide explicitly. See vault `Components-Company-Management.md` → 'Suspended
          companies and new tabs'. While gated, only SuspendedCompanyNav renders, so an item
          added below is hidden and its section shows the disabled screen. */}
      {companyGated ? (
        <SuspendedCompanyNav
          activeSection={activeSection}
          companyName={companyAccessGate.companyName}
          onSectionChange={handleSectionChange}
          productUpdatesUnread={productUpdatesUnread}
          tooltipPlacement={navTooltipPlacement}
        />
      ) : (<>
      {/* Global admins bypass access guards */}
      {(globalAdminLikeForBypass || !currentCompany?.id) ? (
        <Tooltip
          title={companyDisabledState.isDisabled ? "Dashboard access disabled - Company account is disabled" : ""}
          placement={navTooltipPlacement}
        >
          <span>
            <MenuListItem
              data-mainmenu-section="publicLobby"
              tabIndex={-1}
              $active={activeSection === 'publicLobby'}
              onClick={() => !companyDisabledState.isDisabled && handleSectionChange('publicLobby')}
              sx={{
                opacity: companyDisabledState.isDisabled ? 0.5 : 1,
                cursor: companyDisabledState.isDisabled ? 'not-allowed' : 'pointer',
                '&:hover': {
                  backgroundColor: companyDisabledState.isDisabled ? 'transparent' : undefined
                }
              }}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </MenuListItem>
          </span>
        </Tooltip>
      ) : (
        <DashboardAccessGuard
          authReady={parseSessionReady}
          isGlobalAdmin={globalAdminLikeForBypass}
          fallbackComponent={
            <Tooltip
              title={companyDisabledState.isDisabled ? "Dashboard access disabled - Company account is disabled" : "Dashboard access restricted - Check subscription status"}
              placement={navTooltipPlacement}
            >
              <span>
                <MenuListItem
                  data-mainmenu-section="publicLobby"
                  tabIndex={-1}
                  $active={activeSection === 'publicLobby'}
                  onClick={() => { }}
                  sx={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </MenuListItem>
              </span>
            </Tooltip>
          }
        >
          <Tooltip
            title={companyDisabledState.isDisabled ? "Dashboard access disabled - Company account is disabled" : ""}
            placement={navTooltipPlacement}
          >
            <span>
              <MenuListItem
                data-mainmenu-section="publicLobby"
                tabIndex={-1}
                $active={activeSection === 'publicLobby'}
                onClick={() => !companyDisabledState.isDisabled && handleSectionChange('publicLobby')}
                sx={{
                  opacity: companyDisabledState.isDisabled ? 0.5 : 1,
                  cursor: companyDisabledState.isDisabled ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: companyDisabledState.isDisabled ? 'transparent' : undefined
                  }
                }}
              >
                <ListItemIcon>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </MenuListItem>
            </span>
          </Tooltip>
        </DashboardAccessGuard>
      )}

      {/* Global admins bypass access guards */}
      {(globalAdminLikeForBypass) ? (
        <Tooltip
          title={companyDisabledState.isDisabled ? "Room access disabled - Company account is disabled" : ""}
          placement={navTooltipPlacement}
        >
          <span>
            <MenuListItem
              data-mainmenu-section="myRooms"
              tabIndex={-1}
              $active={activeSection === 'myRooms'}
              onClick={() => !companyDisabledState.isDisabled && handleSectionChange('myRooms')}
              sx={{
                opacity: companyDisabledState.isDisabled ? 0.5 : 1,
                cursor: companyDisabledState.isDisabled ? 'not-allowed' : 'pointer',
                '&:hover': {
                  backgroundColor: companyDisabledState.isDisabled ? 'transparent' : undefined
                }
              }}
            >
              <ListItemIcon>
                <GroupsIcon />
              </ListItemIcon>
              <ListItemText primary="Rooms" />
            </MenuListItem>
          </span>
        </Tooltip>
      ) : (
        <AccountAccessGuard
          action="join_room"
          authReady={parseSessionReady}
          isGlobalAdmin={globalAdminLikeForBypass}
          fallbackComponent={
            <Tooltip
              title={!canJoinRooms ? "Room access restricted - Check subscription status" : "Room access disabled - Company account is disabled"}
              placement={navTooltipPlacement}
            >
              <span>
                <MenuListItem
                  data-mainmenu-section="myRooms"
                  tabIndex={-1}
                  $active={activeSection === 'myRooms'}
                  onClick={() => { }}
                  sx={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <ListItemIcon>
                    <GroupsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Rooms" />
                </MenuListItem>
              </span>
            </Tooltip>
          }
        >
          <Tooltip
            title={companyDisabledState.isDisabled ? "Room access disabled - Company account is disabled" : ""}
            placement={navTooltipPlacement}
          >
            <span>
              <MenuListItem
                data-mainmenu-section="myRooms"
                tabIndex={-1}
                $active={activeSection === 'myRooms'}
                onClick={() => !companyDisabledState.isDisabled && handleSectionChange('myRooms')}
                sx={{
                  opacity: companyDisabledState.isDisabled ? 0.5 : 1,
                  cursor: companyDisabledState.isDisabled ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: companyDisabledState.isDisabled ? 'transparent' : undefined
                  }
                }}
              >
                <ListItemIcon>
                  <GroupsIcon />
                </ListItemIcon>
                <ListItemText primary="Rooms" />
              </MenuListItem>
            </span>
          </Tooltip>
        </AccountAccessGuard>
      )}

      <MenuListItem
        data-mainmenu-section="meetings"
        tabIndex={-1}
        $active={activeSection === 'meetings'}
        onClick={() => handleSectionChange('meetings')}
      >
        <ListItemIcon>
          <EventNoteIcon />
        </ListItemIcon>
        <ListItemText primary="Meetings" />
      </MenuListItem>

      {!isGuestUserId(userData?.id) && (
        <MenuListItem
          data-mainmenu-section="chat"
          tabIndex={-1}
          $active={activeSection === 'chat'}
          onClick={() => {
            // A click is a user gesture, which Safari requires for the permission prompt.
            void requestNotificationPermission()
            handleSectionChange('chat')
          }}
        >
          <ListItemIcon>
            <ChatNavDot>
              <ChatIcon />
            </ChatNavDot>
          </ListItemIcon>
          <ListItemText primary="Chat" />
        </MenuListItem>
      )}

      <MenuListItem
        data-mainmenu-section="profile"
        tabIndex={-1}
        $active={activeSection === 'profile'}
        onClick={() => handleSectionChange('profile')}
      >
        <ListItemIcon>
          <PersonIcon />
        </ListItemIcon>
        <ListItemText primary="My Profile" />
      </MenuListItem>

      <Tooltip
        title={
          productUpdatesActiveVoting
            ? "Release notes and roadmap — community voting is open"
            : 'Release notes and product roadmap'
        }
        placement={navTooltipPlacement}
      >
        <MenuListItem
          $active={false}
          onClick={() => window.dispatchEvent(new CustomEvent('openProductUpdates'))}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <Badge
              color="error"
              variant="dot"
              invisible={!productUpdatesUnread}
              sx={{
                '& .MuiBadge-badge': { top: 6, right: 6 },
              }}
            >
              <NewReleasesIcon />
            </Badge>
          </ListItemIcon>
          <ListItemText primary={"What's new"} />
        </MenuListItem>
      </Tooltip>

      <MenuListItem
        $active={false}
        onClick={() => window.dispatchEvent(new CustomEvent('openFeedbackDrawer'))}
        sx={{ cursor: 'pointer' }}
      >
        <ListItemIcon>
          <FeedbackOutlined />
        </ListItemIcon>
        <ListItemText primary="Leave Feedback" />
      </MenuListItem>

      {/* Company Management - Always visible for managers/admins, even on free tier */}
      {!companyPermissionsLoading && !hideCompanyItemsUntilGateAnswers && (
        globalAdminLikeForBypass ||
        isCompanyManagerOrAdmin ||
        companyCanAccessCompanyManagement ||
        hasCompanyAccess ||
        companyDisabledState.showCompanyManagementAccess
      ) && (
          /* Global admins and users with company access bypass access guards */
          (globalAdminLikeForBypass || isCompanyManagerOrAdmin || companyCanAccessCompanyManagement || hasCompanyAccess) ? (
            <MenuListItem
              data-mainmenu-section="companyManagement"
              tabIndex={-1}
              $active={isCompanyManagementNavActive(activeSection)}
              onClick={() => handleSectionChange('companyManagement')}
            >
              <ListItemIcon>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText primary={globalAdminLikeForBypass ? "Company Management" : "My Company"} />
            </MenuListItem>
          ) : (
            <CompanyManagementGuard
              authReady={parseSessionReady}
              isGlobalAdmin={globalAdminLikeForBypass}
              fallbackComponent={
                <Tooltip
                  title={!companyCanAccessCompanyManagement ? "Company Management access restricted - Manager/Admin role required" : ""}
                  placement={navTooltipPlacement}
                >
                  <span>
                    <MenuListItem
                      data-mainmenu-section="companyManagement"
                      tabIndex={-1}
                      $active={isCompanyManagementNavActive(activeSection)}
                      onClick={() => { }}
                      sx={{
                        opacity: 0.5,
                        cursor: 'not-allowed',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <BusinessIcon />
                      </ListItemIcon>
                      <ListItemText primary={globalAdminLikeForBypass ? "Company Management" : "My Company"} />
                    </MenuListItem>
                  </span>
                </Tooltip>
              }
            >
              <MenuListItem
                data-mainmenu-section="companyManagement"
                tabIndex={-1}
                $active={isCompanyManagementNavActive(activeSection)}
                onClick={() => handleSectionChange('companyManagement')}
              >
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText primary={globalAdminLikeForBypass ? "Company Management" : "My Company"} />
              </MenuListItem>
            </CompanyManagementGuard>
          )
        )}

      {/* Self-serve: only when the cloud answered that the user has no company; a lookup that got
          no answer (slow Back4App) is retried instead of offering to create one. */}
      {!companyPermissionsLoading &&
        !companyUnknown &&
        !hideCompanyItemsUntilGateAnswers &&
        !isRegistrationClosed &&
        !hasCompanyAccess &&
        !globalAdminLikeForBypass &&
        !isCompanyManagerOrAdmin && (
        <MenuListItem
          $active={activeSection === 'companySetup'}
          onClick={() => handleSectionChange('companySetup')}
        >
          <ListItemIcon>
            <AddCircleIcon />
          </ListItemIcon>
          <ListItemText primary="Create Company" />
        </MenuListItem>
      )}

      {(userData?.role === 'admin' || localStorage.getItem('forceAdmin') === 'true') && (
        <MenuListItem
          $active={activeSection === 'adminMenu'}
          onClick={() => handleSectionChange('adminMenu')}
        >
          <ListItemIcon>
            <AdminPanelSettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Admin" />
        </MenuListItem>
      )}
      </>)}
    </MenuList>

    <UserProfile>
      <Box
        sx={{
          p: 2,
          width: '100%',
          backgroundColor: 'background.paper',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: 1,
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: displayPrimaryColor,
            backgroundColor: 'background.paper',
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${displayPrimaryColor} 0%, ${theme.palette.primary.light} 100%)`
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${alpha(displayPrimaryColor, 0.25)}, ${alpha(theme.palette.primary.light, 0.25)})`
              }}
            />
            <ProfileAvatar {...profileAvatarPropsOf(userData)} name={getUserFullName()} size={48} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {getUserFullName()}
            </Typography>
            {companyPermissionsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                <CircularProgress size={12} sx={{ color: theme.palette.primary.light, mr: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Loading company...
                </Typography>
              </Box>
            ) : currentCompany ? (
              <Tooltip title={`${currentCompany.name}${currentCompany.domain ? ` (${currentCompany.domain})` : ''}`} placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25, cursor: 'pointer' }}>
                  <BusinessIcon sx={{ fontSize: '0.9rem', color: displayPrimaryColor, mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentCompany.name}
                  </Typography>
                </Box>
              </Tooltip>
            ) : null}
            {/* No billing wording while the company is suspended (SC-409). */}
            {!companyGated &&
              !hideCompanyItemsUntilGateAnswers &&
              !accountStatusLoading &&
              accountStatus && (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={(serverVerifiedElevated)
                    ? 'Global Admin'
                    : (
                      accountStatus?.company
                        ? (accountStatus?.effectiveTier?.name || (accountStatus?.company?.billingTier || '').toString())
                        : (
                          accountStatus?.effectiveTier?.name
                          || (subscriptionStatus === 'trialing' ? 'Trial Period'
                            : subscriptionStatus === 'past_due' ? 'Payment Overdue'
                              : subscriptionStatus === 'canceled' ? 'Subscription Canceled'
                                : 'Free Tier')
                        )
                    )}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: '18px',
                    ...subscriptionStatusChipSx(
                      theme,
                      subscriptionChipToneFromState(serverVerifiedElevated, subscriptionStatus)
                    ),
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={() => setLogoutConfirmOpen(true)}
            startIcon={<LogoutIcon fontSize="small" />}
            sx={{
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              px: 1.25,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease-in-out',
              backgroundColor: 'background.paper',
              '&:hover': {
                backgroundColor: alpha(displayPrimaryColor, 0.1),
                borderColor: displayPrimaryColor,
                color: displayPrimaryColor,
              }
            }}
          >
            Log out
          </Button>
        </Box>
      </Box>
    </UserProfile>
    </>
  )

  return (
    <Fade in timeout={prefersReducedMotion ? 0 : 800}>
      <Backdrop>
        {returningToMaps && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: (t) => t.zIndex.modal + 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
            }}
          >
            <CircularProgress aria-label="Opening Maps" />
          </Box>
        )}
        {/* Global subscription/paywall overlay for managers/admins when action is required */}
        {(() => {
          const problematicSubscription = ['none', 'free_tier_exceeded', 'canceled', 'cancelled', 'suspended'].includes(subscriptionStatus)
          const problematicPayment = ['overdue', 'failed', 'suspended'].includes(paymentStatus)
          const isOnCompanyRoute = routingStore.currentPath?.startsWith('/company')
          // Only show overlay for users who can't access company management (they need to be directed to fix billing)
          // Never over the suspended-company screen: no billing wording for an admin suspension (SC-409).
          const shouldShowOverlay = !companyGated && needsAction && (problematicSubscription || problematicPayment) && !canAccessCompanyManagement && !isCompanyManagerOrAdmin && !isOnCompanyRoute
          return shouldShowOverlay ? <SubscriptionRequiredOverlay /> : null
        })()}
        <MenuContainer elevation={0}>
          {!isMobile ? (
            <Sidebar>
              {renderSidebarNav()}
            </Sidebar>
          ) : null}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {isMobile ? (
              <AppBar
                position="sticky"
                elevation={0}
                color="primary"
                sx={{
                  flexShrink: 0,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'var(--sevv-shell-header-bg, var(--sevv-primary, #f97415))',
                  color: 'primary.contrastText',
                  pt: 'env(safe-area-inset-top)',
                }}
              >
                <Toolbar variant="dense" disableGutters sx={{ px: 1, gap: 1, minHeight: 48, alignItems: 'center' }}>
                  <IconButton
                    ref={menuButtonRef}
                    edge="start"
                    color="inherit"
                    aria-label="Open navigation menu"
                    onClick={() => setNavDrawerOpen(true)}
                    size="large"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Tooltip title={`${displayName} · ${appBarSectionTitle}`}>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <Typography variant="caption" component="div" noWrap sx={{ color: alpha(theme.palette.primary.contrastText, 0.85), lineHeight: 1.2 }}>
                        {appBarSectionTitle}
                      </Typography>
                      <Typography variant="subtitle1" component="div" noWrap sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                        {displayName}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Toolbar>
              </AppBar>
            ) : null}
            <ContentPanel>{renderContent()}</ContentPanel>
          </Box>
        </MenuContainer>

        {isMobile ? (
          <Drawer
            anchor="left"
            open={navDrawerOpen}
            onClose={handleNavDrawerClose}
            PaperProps={{
              ref: drawerPaperRef,
              'aria-label': 'Main navigation',
              sx: {
                width: { xs: 'min(100vw - 16px, 320px)', sm: 300 },
                boxSizing: 'border-box',
                pt: 'env(safe-area-inset-top)',
                pb: 'env(safe-area-inset-bottom)',
                bgcolor: 'var(--sevv-shell-sidebar-bg, var(--sevv-bg-paper))',
              },
            }}
          >
            <Sidebar
              sx={{
                height: '100%',
                maxHeight: '100dvh',
                borderRight: 'none',
                boxShadow: 'none',
                width: '100%',
              }}
            >
              {renderSidebarNav()}
            </Sidebar>
          </Drawer>
        ) : null}


        {/* Dialogs */}
        <BookingFormDialog />
        {/* The join and create dialogs are gated on the render too, so a handler resuming after
            a suspension cannot show one for the commit before the gate effect closes it. */}
        <PasswordDialog
          open={showPasswordDialog && !companyGated}
          onClose={resetPasswordDialog}
          aria-labelledby="password-dialog-title"
        >
          <DialogTitle id="password-dialog-title">Enter Room Password</DialogTitle>
          <DialogContent>
            <form onSubmit={handlePasswordSubmit}>
              <TextField
                autoFocus
                margin="dense"
                label="Password"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={showPasswordError}
                helperText={showPasswordError ? 'Incorrect password' : ''}
              />
              {showPasswordError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Incorrect password. Please try again.
                </Alert>
              )}
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetPasswordDialog} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={(e) => handlePasswordSubmit(e as any)}
              color="primary"
              disabled={!password}
            >
              {roomsLoading ? <CircularProgress size={24} /> : 'Join Room'}
            </Button>
          </DialogActions>
        </PasswordDialog>

        <PermissionsDialog
          open={showPermissionsDialog && !companyGated}
          onClose={() => {
            setShowPermissionsDialog(false)
            setPendingRoomJoin(null)
            setIsLoading(false)
            setInviteJoinNearHost(true)
            setInviteLinkGate({ loading: false, blocked: false })
          }}
          onPermissionsGranted={handlePermissionsGranted}
          network={isPhaserGameReady() ? (getPhaserGameSync()?.scene?.keys?.bootstrap as Bootstrap)?.network : undefined}
          inviteGate={pendingRoomJoin?.inviteToken ? inviteLinkGate : undefined}
          inviteJoinNear={
            pendingRoomJoin?.fromInviteLink && pendingRoomJoin?.sharerUserIdForNear
              ? {
                  visible: true,
                  checked: inviteJoinNearHost,
                  onChange: setInviteJoinNearHost,
                }
              : undefined
          }
        />

        {/* Duplicate Login Confirmation Dialog */}
        <Dialog
          open={!!duplicateLoginConfirmPromise}
          onClose={() => {
            duplicateLoginConfirmPromise?.resolve(false)
            setDuplicateLoginConfirmPromise(null)
          }}
          PaperProps={{
            sx: {
              bgcolor: 'var(--sevv-surface-overlay, #1b1b2f)',
              color: 'var(--sevv-text-primary, #ffffff)',
              borderRadius: '12px',
              border: '1px solid var(--sevv-border-subtle, rgba(255, 255, 255, 0.1))',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
              maxWidth: '400px',
              width: '100%',
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontWeight: 'bold', borderBottom: '1px solid var(--sevv-border-subtle, rgba(255, 255, 255, 0.05))', pb: 2 }}>
            <WarningIcon sx={{ color: 'var(--sevv-warning, #ff9800)' }} />
            Already Logged In
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Typography variant="body1" sx={{ color: 'var(--sevv-text-secondary, #b0b0cf)' }}>
              This character is already connected in a room. Do you want to disconnect the other session and connect here?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid var(--sevv-border-subtle, rgba(255, 255, 255, 0.05))' }}>
            <Button
              onClick={() => {
                duplicateLoginConfirmPromise?.resolve(false)
                setDuplicateLoginConfirmPromise(null)
              }}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                duplicateLoginConfirmPromise?.resolve(true)
                setDuplicateLoginConfirmPromise(null)
              }}
              sx={{
                backgroundColor: 'var(--sevv-primary, #6200ee)',
                color: '#ffffff',
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'var(--sevv-primary-dark, #3700b3)',
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Profile Edit Dialog */}
        <ProfileEditDialog
          open={showProfileDialog}
          onClose={() => {
            setShowProfileDialog(false)
            // Return URL to base profile when closing
            if (routingStore.currentPath === '/profile/edit') {
              routingStore.navigateTo('/profile')
            }
          }}
          forceRequired={false}
          onProfileComplete={(updatedUserData) => {
            console.log('[MainMenu] Profile updated successfully:', updatedUserData)
            // The profile data is already updated in Redux store by ProfileEditDialog
            // This callback can be used for any additional MainMenu-specific updates
          }}
        />

        {/* Logging out is easy to hit by accident and, for an invited user who has
            not set a password yet, hard to recover from. Confirm first. [SC-313] */}
        <Dialog
          open={logoutConfirmOpen}
          onClose={() => setLogoutConfirmOpen(false)}
          aria-labelledby="mainmenu-logout-confirm-title"
        >
          <DialogTitle id="mainmenu-logout-confirm-title">Log out of Sevv?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You&apos;ll need to sign in again to get back to your workspace.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLogoutConfirmOpen(false)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleLogout}
              variant="contained"
              color="primary"
              sx={{ textTransform: 'none' }}
            >
              Log out
            </Button>
          </DialogActions>
        </Dialog>

      </Backdrop>
    </Fade>
  )
}
